/**
 * Task Rules Tab — Stage 0 Configuration UI
 *
 * Edit task templates, constraint builder, sub-team management,
 * and real-time preflight validation panel.
 */

import {
  type CertificationDefinition,
  Level,
  type LoadFormula,
  type LoadFormulaSnapshotEntry,
  type LoadWindow,
  type OneTimeTask,
  type PreflightResult,
  PreflightSeverity,
  type RestRule,
  type SleepRecoveryRule,
  type SlotTemplate,
  type SubTeamTemplate,
  type TaskSet,
  type TaskTemplate,
} from '../models/types';
import { detectStale } from '../shared/utils/load-formula';
import { initAddTemplateModal, openAddTemplateModal } from './add-template-modal';
import * as store from './config-store';
import { closeGroupedSlotEdit, initGroupedSlotEdit, openGroupedSlotEdit } from './grouped-slot-edit';
import { initLoadFormulaModal, openLoadFormulaModal } from './load-formula-modal';
import { runPreflight } from './preflight';
import { escAttr, escHtml, formatMetadataDate, SVG_ICONS } from './ui-helpers';
import { showAlert, showConfirm, showPrompt, showToast } from './ui-modal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isCriticalOrWarning(severity: PreflightSeverity): boolean {
  return severity === PreflightSeverity.Critical || severity === PreflightSeverity.Warning;
}

function getRelevantFindingsForTemplate(tpl: TaskTemplate, pf: PreflightResult) {
  return pf.findings.filter((f) => f.templateId === tpl.id && isCriticalOrWarning(f.severity));
}

function getRelevantFindingsForOneTime(ot: OneTimeTask, pf: PreflightResult) {
  const slotIds = new Set<string>();
  for (const s of ot.slots) slotIds.add(s.id);
  for (const st of ot.subTeams) for (const s of st.slots) slotIds.add(s.id);
  return pf.findings.filter((f) => {
    if (!isCriticalOrWarning(f.severity)) return false;
    if (f.oneTimeTaskId === ot.id) return true;
    return !!f.slotId && slotIds.has(f.slotId);
  });
}

const FIELD_LABELS: Record<string, string> = {
  durationHours: 'משך',
  shiftsPerDay: 'משמרות/יום',
  startHour: 'שעת התחלה',
  startMinute: 'דקת התחלה',
};

/** Show a warning toast if any numeric field was clamped by sanitization. */
function notifyIfClamped(raw: Record<string, number | undefined>, sanitized: Record<string, number | undefined>): void {
  const corrections: string[] = [];
  for (const key of Object.keys(raw)) {
    const r = raw[key],
      s = sanitized[key];
    if (r !== undefined && s !== undefined && r !== s) {
      // Wrap the numeric "raw → sanitized" transition in an LRI/PDI bidi isolate
      // (U+2066 … U+2069) so it renders LTR inside the Hebrew toast, keeping the
      // older value on the left and the corrected value on the right.
      corrections.push(`${FIELD_LABELS[key] || key}: \u2066${r} → ${s}\u2069`);
    }
  }
  if (corrections.length) {
    const rawDur = raw.durationHours;
    const rawShifts = raw.shiftsPerDay;
    const durOrShiftsClamped =
      (rawDur !== undefined && sanitized.durationHours !== undefined && rawDur !== sanitized.durationHours) ||
      (rawShifts !== undefined && sanitized.shiftsPerDay !== undefined && rawShifts !== sanitized.shiftsPerDay);
    const productOverflow = rawDur !== undefined && rawShifts !== undefined && rawDur * rawShifts > 24;
    const suffix = durOrShiftsClamped && productOverflow ? ' (משך × משמרות לא יכולים לחרוג מ-24 שעות ביום)' : '';
    showToast(`ערכים לא תקינים תוקנו: ${corrections.join(', ')}${suffix}`, {
      type: 'warning',
      duration: 5000,
    });
  }
}

/**
 * Check whether a task name (case-insensitive, trimmed) is already taken by a
 * task template or a one-time task. `excludeId` skips a single record (used
 * when renaming so the item doesn't collide with its own current name).
 */
function isTaskNameTaken(name: string, excludeId?: string): boolean {
  const key = name.trim().toLowerCase();
  if (!key) return false;
  for (const t of store.getAllTaskTemplates()) {
    if (t.id === excludeId) continue;
    if (t.name.trim().toLowerCase() === key) return true;
  }
  for (const ot of store.getAllOneTimeTasks()) {
    if (ot.id === excludeId) continue;
    if (ot.name.trim().toLowerCase() === key) return true;
  }
  return false;
}

const LEVEL_OPTIONS = [Level.L0, Level.L2, Level.L3, Level.L4];
function getCertOptions(): CertificationDefinition[] {
  return store.getCertificationDefinitions();
}

function templateBadge(tpl: { color?: string; name: string }): string {
  const color = tpl.color || '#7f8c8d';
  return `<span class="badge" style="background:${color}">${escHtml(tpl.name)}</span>`;
}

/** A single shift descriptor — 1-based index + display label like "06:00–14:00". */
type ShiftDescriptor = { idx: number; label: string };

/** Format a clock-time-of-day (in minutes), wrapping past 24:00 to next-day clock. */
function fmtClockMinutes(totalMinutes: number): string {
  const dayMin = 24 * 60;
  const m = ((totalMinutes % dayMin) + dayMin) % dayMin;
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Materialise (startMinutes, durationMinutes, count) into per-shift descriptors. */
function computeShiftDescriptors(startMinutes: number, durationMinutes: number, count: number): ShiftDescriptor[] {
  const out: ShiftDescriptor[] = [];
  if (count < 1 || durationMinutes <= 0) return out;
  for (let i = 0; i < count; i++) {
    const s = startMinutes + i * durationMinutes;
    const e = s + durationMinutes;
    out.push({ idx: i + 1, label: `${fmtClockMinutes(s)}–${fmtClockMinutes(e)}` });
  }
  return out;
}

/** Shift descriptors for a recurring template. */
function shiftsForTemplate(tpl: TaskTemplate): ShiftDescriptor[] {
  return computeShiftDescriptors(tpl.startHour * 60, tpl.durationHours * 60, tpl.shiftsPerDay);
}

/** Single shift descriptor for a one-time task — its own time block. */
function shiftsForOneTime(ot: { startHour: number; startMinute: number; durationHours: number }): ShiftDescriptor[] {
  const startMin = ot.startHour * 60 + (ot.startMinute || 0);
  const durMin = ot.durationHours * 60;
  if (durMin <= 0) return [{ idx: 1, label: '' }];
  return [{ idx: 1, label: `${fmtClockMinutes(startMin)}–${fmtClockMinutes(startMin + durMin)}` }];
}

/**
 * Render a horizontal strip of chips that materialize the abstract
 * (startHour, durationHours, shiftsPerDay) trio into a concrete shift list,
 * so the user can see what those numbers actually produce. Wrap-around past
 * 24:00 displays as the next day's wall-clock time (no midnight-crossing
 * label, per project convention).
 */
function renderShiftPreview(tpl: TaskTemplate): string {
  const shifts = shiftsForTemplate(tpl);
  if (shifts.length === 0) return '';
  const chips = shifts
    .map(
      (s) =>
        `<span class="tprop-shift-chip"><span class="tprop-shift-chip-idx">${s.idx}</span><span class="tprop-shift-chip-range" dir="ltr">${s.label}</span></span>`,
    )
    .join('');
  return `<div class="tprop-shift-preview" aria-label="רשימת משמרות ביום">
    <span class="tprop-shift-preview-label">משמרות ביום</span>
    <div class="tprop-shift-preview-chips">${chips}</div>
  </div>`;
}

/** Check if any slot in a template references a deleted certification. */
function hasOrphanedSlotCerts(tpl: {
  slots: { requiredCertifications: string[]; forbiddenCertifications?: string[] }[];
  subTeams: { slots: { requiredCertifications: string[]; forbiddenCertifications?: string[] }[] }[];
}): boolean {
  const activeCertIds = new Set(getCertOptions().map((d) => d.id));
  const allSlots = [...tpl.slots, ...tpl.subTeams.flatMap((st) => st.slots)];
  return allSlots.some(
    (s) =>
      s.requiredCertifications.some((c) => !activeCertIds.has(c)) ||
      (s.forbiddenCertifications || []).some((c) => !activeCertIds.has(c)),
  );
}

function levelBadge(level: Level): string {
  const colors = ['#95a5a6', '#3498db', '#2ecc71', '#e67e22', '#e74c3c'];
  return `<span class="badge badge-sm" style="background:${colors[level]}">${level}</span>`;
}

function certBadge(certId: string): string {
  const def = store.getCertificationById(certId);
  if (!def) {
    return `<span class="badge badge-sm badge-orphan" title="הסמכה שנמחקה: ${escHtml(certId)}">⚠ ${escHtml(certId)}</span>`;
  }
  if (def.deleted) {
    return `<span class="badge badge-sm badge-orphan" title="הסמכה שנמחקה: ${escHtml(def.label)}">⚠ ${escHtml(def.label)}</span>`;
  }
  return `<span class="badge badge-sm" style="background:${def.color}">${escHtml(def.label)}</span>`;
}

function forbiddenCertBadge(certId: string): string {
  const def = store.getCertificationById(certId);
  if (!def) {
    return `<span class="badge badge-sm badge-orphan" title="הסמכה אסורה שנמחקה: ${escHtml(certId)}" style="text-decoration:line-through">⚠ ${escHtml(certId)}</span>`;
  }
  if (def.deleted) {
    return `<span class="badge badge-sm badge-orphan" title="הסמכה אסורה שנמחקה: ${escHtml(def.label)}" style="text-decoration:line-through">⚠ ${escHtml(def.label)}</span>`;
  }
  return `<span class="badge badge-sm" style="background:#c0392b;text-decoration:line-through">${escHtml(def.label)}</span>`;
}

/** Badge for a rest rule assignment (header display). */
function _restRuleBadge(restRuleId?: string): string {
  if (!restRuleId) return '';
  const rule = store.getRestRuleById(restRuleId);
  if (!rule) return `<span class="badge badge-sm badge-orphan" title="כלל מרווח שנמחק">⚠ כלל חסר</span>`;
  if (rule.deleted)
    return `<span class="badge badge-sm badge-orphan" title="כלל מרווח שנמחק: ${escHtml(rule.label)}">⚠ ${escHtml(rule.label)}</span>`;
  return `<span class="badge badge-sm badge-outline">${escHtml(rule.label)} ${rule.durationHours} שע׳</span>`;
}

/** Inline orphan warning if a task references a deleted/missing rest rule. */
function _restRuleOrphanNote(restRuleId?: string): string {
  if (!restRuleId) return '';
  const rule = store.getRestRuleById(restRuleId);
  if (!rule || rule.deleted) {
    const label = rule ? rule.label : restRuleId;
    return ` <span class="badge-orphan-label" style="font-size:0.78rem;">⚠ כלל "${escHtml(label)}" נמחק — המרווח לא פעיל</span>`;
  }
  return '';
}

/** Strip English level references (L0, L3/L4, (L2+), etc.) from a slot label. */
function stripLevelText(label: string): string {
  return label
    .replace(/\s*\(?L\d[\d/+L]*\)?\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function fmtHm(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Read back the HC-15 "Sleep & Recovery" fields from the editable body.
 *
 * The rule's "active" state is derived from the chip selection — at least one
 * trigger shift must be checked for the rule to exist. The parser returns one
 * of:
 *  - 'preserve': body is not rendered (section collapsed) → leave stored rule alone.
 *  - 'set' (rule): body is rendered with ≥1 chip + valid hours → persist rule.
 *  - 'clear': body is rendered with zero chips → delete rule. The current hours
 *    value is cached so re-activation later restores it instead of snapping
 *    back to the default.
 *  - 'invalid': body is rendered with chips but hours are out of range → caller
 *    shows an error toast and bails.
 */
type ParsedSleepRecovery =
  | { kind: 'preserve' }
  | { kind: 'set'; value: SleepRecoveryRule }
  | { kind: 'clear' }
  | { kind: 'invalid'; reason: string };

const SLEEP_RECOVERY_MAX_HOURS = 24;
const DEFAULT_RECOVERY_HOURS = 7;

/**
 * Cache the user's last-typed recovery hours per template/one-time task. Read
 * by the renderer when the rule is currently inactive (no chips selected) so
 * the hours field shows what the user had instead of snapping to the default.
 * Written by the parser when it sees a 'clear' transition.
 */
const lastSleepRecoveryHours = new Map<string, number>();

function parseSleepRecoveryInput(scope: HTMLElement, target: 'tpl' | 'ot', id: string): ParsedSleepRecovery {
  const attr = target === 'tpl' ? 'tpl' : 'ot';
  const chipContainer = scope.querySelector(`[data-${attr}-field="sleepRecoveryShifts"]`) as HTMLElement | null;
  if (!chipContainer) {
    // Body not rendered (section collapsed) → leave stored rule alone.
    return { kind: 'preserve' };
  }

  const chips = Array.from(chipContainer.querySelectorAll('[data-action="toggle-shift-chip"]')) as HTMLElement[];
  const triggerShifts: number[] = [];
  for (const chip of chips) {
    if (chip.getAttribute('aria-checked') !== 'true') continue;
    const idx = parseInt(chip.dataset.shiftIdx ?? '', 10);
    if (Number.isFinite(idx) && idx >= 1) triggerShifts.push(idx);
  }
  triggerShifts.sort((a, b) => a - b);

  const hoursRaw = (scope.querySelector(`[data-${attr}-field="sleepRecoveryHours"]`) as HTMLInputElement | null)?.value;
  const trimmed = (hoursRaw ?? '').trim();
  const hoursNumeric = Number(trimmed);
  const hoursValid =
    trimmed !== '' && Number.isFinite(hoursNumeric) && hoursNumeric >= 1 && hoursNumeric <= SLEEP_RECOVERY_MAX_HOURS;

  if (triggerShifts.length === 0) {
    // Cache user-typed hours so that re-activating the rule restores them.
    if (hoursValid) lastSleepRecoveryHours.set(`${target}:${id}`, Math.floor(hoursNumeric));
    return { kind: 'clear' };
  }

  if (!hoursValid) {
    return { kind: 'invalid', reason: `משך התאוששות לא תקין (1–${SLEEP_RECOVERY_MAX_HOURS} שעות)` };
  }
  return {
    kind: 'set',
    value: { triggerShifts, recoveryHours: Math.floor(hoursNumeric) },
  };
}

/**
 * Build the header summary chip text (shown when section is collapsed).
 *  - 0 selected (invalid state): "אין משמרות מסומנות"
 *  - all selected: "כל המשמרות"
 *  - single: "משמרת N"
 *  - multiple: "משמרות 1,3"
 */
function summariseTriggerShifts(selected: number[], total: number): string {
  if (selected.length === 0) return 'אין משמרות מסומנות';
  if (total > 0 && selected.length === total) return 'כל המשמרות';
  const sorted = [...selected].sort((a, b) => a - b);
  if (sorted.length === 1) return `משמרת ${sorted[0]}`;
  return `משמרות ${sorted.join(',')}`;
}

/**
 * Render the HC-15 "Sleep & Recovery" editor section. Used by both
 * TaskTemplate and OneTimeTask editors.
 *
 * The rule's "active" state is fully derived from the chip selection — there
 * is no separate enable/disable switch. Selecting the first chip creates the
 * rule with the displayed recovery-hours value; clearing the last chip deletes
 * it. The header is purely an expand/collapse affordance.
 *
 * Layout:
 *  - Header row (always clickable): title, status pill (active summary or
 *    muted "לא פעיל" placeholder), arrow.
 *  - Body (rendered when expanded): shift chips + recovery-hours input. Both
 *    are always editable; the chips alone gate "active." When active, an
 *    "active" badge in the header and a colored ring on the section card make
 *    the state visible without expanding.
 */
function renderSleepRecoveryEditor(
  rule: SleepRecoveryRule | undefined,
  target: 'tpl' | 'ot',
  id: string,
  shifts: ShiftDescriptor[],
): string {
  const active = !!rule;
  const triggerShifts = rule?.triggerShifts ?? [];
  const expandKey = `${target}:${id}`;
  // Hours fall back to last-typed (cached on rule clear) so reactivating the
  // rule restores the user's preferred duration, not the global default.
  const hours = rule?.recoveryHours ?? lastSleepRecoveryHours.get(expandKey) ?? DEFAULT_RECOVERY_HOURS;
  const attr = target === 'tpl' ? 'tpl' : 'ot';
  const idAttr = target === 'tpl' ? 'tid' : 'ot-id';
  const isExpanded = expandedSleepRecovery.has(expandKey);

  // Effective selection used for rendering — clamp stale indices that exceed
  // the current shift count so the chip strip never shows phantom selections.
  const validIdxs = new Set(shifts.map((s) => s.idx));
  const effectiveSelected = triggerShifts.filter((idx) => validIdxs.has(idx));

  const summary = active
    ? `<span class="sr-summary-chip">${escHtml(summariseTriggerShifts(effectiveSelected, shifts.length))} · ${hours} שע׳</span>`
    : `<span class="sr-summary-off">לא פעיל</span>`;

  const chipHtml = shifts
    .map((s) => {
      const sel = effectiveSelected.includes(s.idx);
      const cls = `tprop-shift-chip tprop-shift-chip--selectable${sel ? ' tprop-shift-chip--selected' : ''}`;
      const tickGlyph = sel ? '<span class="tprop-shift-chip-tick" aria-hidden="true">✓</span>' : '';
      return `<button type="button" class="${cls}" role="checkbox" aria-checked="${sel}"
                data-action="toggle-shift-chip" data-shift-idx="${s.idx}"
                aria-label="משמרת ${s.idx} ${s.label}">
        ${tickGlyph}<span class="tprop-shift-chip-idx">${s.idx}</span><span class="tprop-shift-chip-range" dir="ltr">${escHtml(s.label)}</span>
      </button>`;
    })
    .join('');

  // Bulk actions are only useful for templates with multiple shifts. For a
  // single-shift task, the chip itself is the only control.
  const showBulkActions = shifts.length > 1;
  const bulkActions = showBulkActions
    ? `<div class="sr-shift-bulk">
        <button type="button" class="sr-link-btn" data-action="shift-bulk-select">בחר הכל</button>
        <span class="sr-bulk-sep" aria-hidden="true">·</span>
        <button type="button" class="sr-link-btn" data-action="shift-bulk-clear">נקה</button>
      </div>`
    : '';

  const inactiveHint = !active
    ? `<p class="sr-hint" role="note">בחר משמרות כדי להפעיל את הכלל. לאחר משמרת כזו ייחסם המשתתף ממשימות עם עומס למשך ${hours} שעות.</p>`
    : '';

  const body = isExpanded
    ? `
      <div class="sr-body">
        <div class="sr-field-group">
          <div class="sr-field">
            <div class="sr-field-row sr-field-row--shift-header">
              <label class="sr-field-label">משמרות שמפעילות את הכלל</label>
              ${bulkActions}
            </div>
            <div class="sr-shift-chips" data-${attr}-field="sleepRecoveryShifts" data-${idAttr}="${id}"
                 role="group" aria-label="בחירת משמרות מפעילות">
              ${chipHtml}
            </div>
            ${inactiveHint}
          </div>
          <div class="sr-field">
            <label class="sr-field-label">חסום משימות עם עומס &gt; 0 למשך</label>
            <div class="sr-field-row">
              <input class="input-sm sr-hours" type="number" min="1" max="${SLEEP_RECOVERY_MAX_HOURS}" step="1" inputmode="numeric"
                     data-${attr}-field="sleepRecoveryHours" data-${idAttr}="${id}"
                     value="${hours}" aria-label="מספר שעות" />
              <span class="sr-sep">שעות לאחר סיום המשימה</span>
            </div>
          </div>
        </div>
      </div>`
    : '';

  const sectionCls = [
    'tprop-section',
    'tprop-section--sleep-recovery',
    'sr-collapsible',
    active ? 'sr-active' : 'sr-inactive',
    isExpanded ? 'sr-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Two-row header: row 1 carries the title and arrow; row 2 carries the
  // status pill. Splitting prevents the narrow-column case from forcing the
  // title and pill to fight over the same line and ellipsize each other into
  // illegibility ("השלמות ש…" + "משמרת 2 · 5 …"). The pill is hidden when the
  // section is expanded since the body already shows the source of truth.
  const statusRow = isExpanded ? '' : `<span class="sr-status">${summary}</span>`;

  return `
    <section class="${sectionCls}">
      <button type="button" class="sr-header"
              data-action="toggle-sleep-recovery" data-sr-target="${target}" data-sr-id="${id}"
              aria-expanded="${isExpanded}">
        <span class="sr-title">
          <span class="sr-icon" aria-hidden="true">💤</span>
          <span class="sr-title-text">השלמות שינה והתאוששות</span>
        </span>
        <span class="sr-arrow" aria-hidden="true">${isExpanded ? '▾' : '◂'}</span>
        ${statusRow}
      </button>
      ${body}
    </section>`;
}

/** A Sleep & Recovery rule is "active" iff the rule object exists — mirrors the
 *  `active` flag inside renderSleepRecoveryEditor so the "מתקדם" summary chip and
 *  the editor never disagree about whether the rule is on. */
function isSleepRecoveryActive(rule: SleepRecoveryRule | undefined): boolean {
  return !!rule;
}

/**
 * Collapsible "מתקדם" (advanced) disclosure that wraps the rest-rule select,
 * sleep-recovery editor, and load-windows editor inside an expanded task card.
 * Reuses the proven `.sr-*` collapsible markup/CSS. `innerHtml` is the already-
 * built inner content; `chips` are short tokens shown on the header when the
 * section holds configured (non-default) content, so it isn't missed while
 * collapsed. Open state is module-level (`expandedAdvanced`) so it survives the
 * full re-render fired on every field edit.
 */
function renderAdvancedSection(target: 'tpl' | 'ot', id: string, innerHtml: string, chips: string[]): string {
  const open = expandedAdvanced.has(`${target}:${id}`);
  const statusRow = chips.length
    ? `<span class="sr-status"><span class="sr-summary-chip">${escHtml(chips.join(' · '))}</span></span>`
    : '';
  return `
    <section class="tprop-section tprop-section--advanced sr-collapsible${open ? ' adv-open' : ''}">
      <button type="button" class="sr-header"
              data-action="toggle-advanced" data-adv-target="${target}" data-adv-id="${id}"
              aria-expanded="${open}">
        <span class="sr-title">
          <span class="sr-icon" aria-hidden="true">⚙️</span>
          <span class="sr-title-text">מתקדם</span>
        </span>
        <span class="sr-arrow" aria-hidden="true">${open ? '▾' : '◂'}</span>
        ${statusRow}
      </button>
      ${open ? `<div class="sr-body adv-body">${innerHtml}</div>` : ''}
    </section>`;
}

/** Build the "מתקדם" header chips for a template / one-time task — one token per
 *  configured advanced setting (rest-rule, sleep-recovery, load-windows). */
function advancedChips(item: {
  restRuleId?: string;
  sleepRecovery?: SleepRecoveryRule;
  loadWindows?: LoadWindow[];
}): string[] {
  const chips: string[] = [];
  if (item.restRuleId) chips.push('מרווח');
  if (isSleepRecoveryActive(item.sleepRecovery)) chips.push('שינה');
  const lw = item.loadWindows?.length ?? 0;
  if (lw > 0) chips.push(lw === 1 ? 'חלון אחד' : `${lw} חלונות`);
  return chips;
}

/** Parse an HH:MM string with range validation (00:00–23:59). */
function parseHm(value: string): { h: number; m: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

/** Treat a load window as a half-open clock-time interval and return the set of
 *  minutes (mod 1440) it covers. End-touching windows (e.g. 05:00–06:30 and
 *  06:30–07:00) do not share any minute, so they don't overlap. Windows where
 *  end ≤ start are interpreted as crossing midnight. */
function loadWindowMinuteSet(w: {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}): Set<number> {
  const start = w.startHour * 60 + w.startMinute;
  let end = w.endHour * 60 + w.endMinute;
  if (end <= start) end += 1440;
  const out = new Set<number>();
  for (let m = start; m < end; m++) out.add(m % 1440);
  return out;
}

function loadWindowsOverlap(
  a: { startHour: number; startMinute: number; endHour: number; endMinute: number },
  b: { startHour: number; startMinute: number; endHour: number; endMinute: number },
): boolean {
  const aMin = loadWindowMinuteSet(a);
  for (const m of loadWindowMinuteSet(b)) if (aMin.has(m)) return true;
  return false;
}

// ─── State ───────────────────────────────────────────────────────────────────

let expandedTemplateId: string | null = null;
let expandedOtId: string | null = null;
let addingSlotTo: { templateId: string; subTeamId?: string; isOneTime?: boolean } | null = null;
let editingSlot: { templateId: string; subTeamId?: string; slotId: string; isOneTime?: boolean } | null = null;
let showAddOneTime = false;
/** IDs of templates / one-time tasks whose Sleep & Recovery section is currently expanded. Collapsed by default. */
const expandedSleepRecovery = new Set<string>();
/** Keys (`${'tpl'|'ot'}:${id}`) of cards whose "מתקדם" (advanced) disclosure is open. Collapsed by default.
 *  Module-level (not a DOM `<details>`) so it survives the full re-render fired on every field edit. */
const expandedAdvanced = new Set<string>();

/**
 * Pending load formula for the still-inline "add one-time task" form. Populated
 * when the user opens the load-formula modal from the form and saves a formula;
 * cleared when the form closes (confirm or cancel) or the load-formula modal
 * clears it. Survives re-renders so reopening the modal shows the in-progress
 * formula. The recurring-template add form has been replaced by add-template-modal,
 * which manages its own pending formula internally.
 */
let _pendingOtLoadFormula: LoadFormula | undefined;

// ─── Task Sets Panel State ───────────────────────────────────────────────────

let _taskSetPanelOpen = false;
let _taskSetFormMode: 'none' | 'save-as' | 'rename' = 'none';
let _taskSetFormError = '';
let _taskSetRenameTargetId: string | null = null;

/** Full reset of module-level view state — called by the tutorial demo
 *  orchestrator on entry/exit so an expanded template card or open panel
 *  from a previous session doesn't leak across the demo boundary. */
export function resetTaskRulesTabViewState(): void {
  expandedTemplateId = null;
  expandedOtId = null;
  addingSlotTo = null;
  editingSlot = null;
  showAddOneTime = false;
  expandedSleepRecovery.clear();
  expandedAdvanced.clear();
  _pendingOtLoadFormula = undefined;
  _taskSetPanelOpen = false;
  _taskSetFormMode = 'none';
  _taskSetFormError = '';
  _taskSetRenameTargetId = null;
  closeGroupedSlotEdit();
}

// ─── Render ──────────────────────────────────────────────────────────────────

export function renderTaskRulesTab(preflight: PreflightResult): string {
  const templates = store.getAllTaskTemplates();
  const criticals = preflight.findings.filter((f) => f.severity === PreflightSeverity.Critical);

  let html = `
  <div class="tab-toolbar">
    <div class="toolbar-left">
      <h2>פירוט משימות <span class="count">${templates.length}</span></h2>
      <div class="score-card inline-badge ${criticals.length > 0 ? 'status-error badge-clickable' : 'status-ok'}"${criticals.length > 0 ? ' role="button" tabindex="0" data-action="show-readiness-findings" title="הצג מה חוסם יצירת שבצ&quot;ק" aria-label="הצג מה חוסם יצירת שבצ&quot;ק"' : ''}>
        <span class="score-value">${criticals.length > 0 ? '✗ חסום' : '✓ מוכן'}</span>
        <span class="score-label">מוכנות לשיבוץ</span>
      </div>
    </div>
    <div class="toolbar-right">
      <button class="btn-sm btn-outline${_taskSetPanelOpen ? ' pill-active' : ''}" data-action="tset-panel-toggle" title="סטים של משימות">📋 סטים${store.isTaskSetDirty() ? ' <span class="dirty-dot"></span>' : ''}</button>
      <button class="btn-primary btn-sm" data-action="toggle-add-template">+ משימה חדשה</button>
    </div>
  </div>`;

  // Task Sets panel
  if (_taskSetPanelOpen) {
    html += renderTaskSetPanel();
  }

  // Template cards (or first-run empty state when there are none)
  if (templates.length === 0) {
    html += renderTaskRulesEmptyState();
  } else {
    html += '<div class="template-list">';
    for (const tpl of templates) {
      html += renderTemplateCard(tpl, preflight);
    }
    html += '</div>';
  }

  // ── One-Time Tasks Section ──
  const oneTimeTasks = store.getAllOneTimeTasks();
  html += `
  <div class="tab-toolbar" style="margin-top:24px; border-top:1px solid var(--border); padding-top:16px;">
    <div class="toolbar-left">
      <h2>משימות חד-פעמיות <span class="count">${oneTimeTasks.length}</span></h2>
    </div>
    <div class="toolbar-right">
      <button class="btn-primary btn-sm" data-action="toggle-add-onetime">+ משימה חד-פעמית</button>
    </div>
  </div>`;

  if (showAddOneTime) {
    html += renderAddOneTimeForm();
  }

  if (oneTimeTasks.length > 0) {
    html += '<div class="template-list">';
    for (const ot of oneTimeTasks) {
      html += renderOneTimeCard(ot, preflight);
    }
    html += '</div>';
  } else if (!showAddOneTime) {
    html += `<p class="text-muted" style="padding:6px 2px;">אין משימות חד-פעמיות.</p>`;
  }

  // ── Rest Rules Management ──
  const restRules = store.getRestRules();
  html += `
  <div class="tab-toolbar" style="margin-top:24px; border-top:1px solid var(--border); padding-top:16px;">
    <div class="toolbar-left">
      <h2 style="display:flex; align-items:center; gap:8px;">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.7;"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        כללי מרווחים מינימליים
        <span class="count">${restRules.length}</span>
      </h2>
    </div>
    <div class="toolbar-right">
      <button class="btn-sm btn-primary" data-action="add-rest-rule">+ הוסף כלל</button>
    </div>
  </div>
  <div class="template-card global-settings-card" style="margin-top:8px;">
    <div style="padding:14px 18px;">
      <span style="font-size:0.78rem; color:var(--text-muted);">משימות המשויכות לאותו כלל (או לכללים שונים) לא ישובצו ברצף — תישמר הפסקה מינימלית ביניהן</span>
      ${
        restRules.length === 0
          ? '<div style="padding:12px 0; color:var(--text-muted); font-size:0.88rem; text-align:center;">אין כללים מוגדרים. לחץ "הוסף כלל" ליצירת כלל חדש.</div>'
          : `
      <table style="width:100%; margin-top:10px; border-collapse:collapse;">
        <thead><tr style="border-bottom:1px solid var(--border);">
          <th style="text-align:right; padding:6px 8px; font-size:0.82rem; font-weight:500;">שם</th>
          <th style="text-align:center; padding:6px 8px; font-size:0.82rem; font-weight:500; width:90px;">שעות</th>
          <th style="width:80px;"></th>
        </tr></thead>
        <tbody>${restRules
          .map(
            (r) => `
          <tr data-rest-rule-id="${r.id}" style="border-bottom:1px solid var(--border-light, var(--border));">
            <td style="padding:8px;">
              <input type="text" maxlength="40" class="input-sm" data-rr-field="label" value="${escHtml(r.label)}" style="width:100%;" />
            </td>
            <td style="padding:8px; text-align:center;">
              <input type="number" class="input-sm" data-rr-field="durationHours" value="${r.durationHours}" min="0.5" max="24" step="0.5" style="width:60px; text-align:center;" />
            </td>
            <td style="padding:8px; text-align:center;">
              <button class="btn-xs btn-danger-outline" data-action="delete-rest-rule" data-rr-id="${r.id}" title="מחק">✕</button>
            </td>
          </tr>
        `,
          )
          .join('')}</tbody>
      </table>`
      }
    </div>
  </div>`;

  return html;
}

// ─── Task Set Panel ──────────────────────────────────────────────────────────

function renderTaskSetPanel(): string {
  const sets = store.getAllTaskSets();
  const activeId = store.getActiveTaskSetId();
  const dirty = store.isTaskSetDirty();
  const nameFieldInvalid = _taskSetFormError ? ' aria-invalid="true" aria-describedby="tset-form-error"' : '';

  let html = `<div class="preset-panel pset-panel">`;

  // Header
  html += `<div class="preset-panel-header">
    <h3>📋 סטים של משימות <span class="count">${sets.length}</span></h3>
    <button class="btn-xs btn-outline" data-action="tset-panel-close" title="סגור">✕</button>
  </div>`;

  // Form area
  if (_taskSetFormMode === 'save-as') {
    html += `<div class="preset-inline-form" id="tset-saveas-form">
      <div class="preset-form-row">
        <label>שם: <input type="text" class="preset-name-input" data-field="tset-saveas-name" maxlength="60" placeholder="הסט שלי" autofocus${nameFieldInvalid} /></label>
        <label>תיאור: <input type="text" class="preset-desc-input" data-field="tset-saveas-desc" maxlength="200" placeholder="תיאור אופציונלי" /></label>
        <button class="btn-sm btn-primary" data-action="tset-saveas-confirm">שמור</button>
        <button class="btn-sm btn-outline" data-action="tset-form-cancel">ביטול</button>
      </div>
      <div class="preset-validation-error" id="tset-form-error">${_taskSetFormError}</div>
    </div>`;
  } else if (_taskSetFormMode === 'rename') {
    const targetId = _taskSetRenameTargetId ?? activeId;
    const target = targetId ? store.getTaskSetById(targetId) : undefined;
    html += `<div class="preset-inline-form" id="tset-rename-form">
      <div class="preset-form-row">
        <label>שם: <input type="text" class="preset-name-input" data-field="tset-rename-name" maxlength="60" value="${escHtml(target?.name ?? '')}"${nameFieldInvalid} /></label>
        <label>תיאור: <input type="text" class="preset-desc-input" data-field="tset-rename-desc" maxlength="200" value="${escHtml(target?.description ?? '')}" /></label>
        <button class="btn-sm btn-primary" data-action="tset-rename-confirm">שמור</button>
        <button class="btn-sm btn-outline" data-action="tset-form-cancel">ביטול</button>
      </div>
      <div class="preset-validation-error" id="tset-form-error">${_taskSetFormError}</div>
    </div>`;
  } else {
    html += `<div class="preset-actions-primary">
      <button class="btn-sm btn-primary" data-action="tset-new">+ שמור סט חדש</button>
    </div>`;
  }

  // Set list
  if (sets.length === 0) {
    html += `<div class="preset-empty"><span class="text-muted">אין סטים שמורים.</span></div>`;
  } else {
    html += `<div class="preset-list">`;
    for (const s of sets) {
      const isActive = s.id === activeId;
      const isBuiltIn = s.builtIn ?? false;
      const count = s.templates.length;
      html += `<div class="preset-item ${isActive ? 'preset-item-active' : ''}" data-tset-id="${s.id}">
        <div class="preset-item-main">
          <span class="preset-item-name">${escHtml(s.name)}</span>
          <span class="pset-count-badge">${count} תבניות</span>
          ${isBuiltIn ? '<span class="preset-builtin-badge">מובנה</span>' : ''}
          ${isActive && dirty ? '<span class="preset-dirty-badge">שונה</span>' : ''}
          ${!isBuiltIn && s.createdAt ? `<span class="library-item-date text-muted">נשמר ${escHtml(formatMetadataDate(s.createdAt))}</span>` : ''}
        </div>
        ${s.description ? `<div class="preset-item-desc text-muted">${escHtml(s.description)}</div>` : ''}
        <div class="preset-item-actions">
          ${!isActive ? `<button class="btn-xs btn-primary" data-tset-action="load" data-tset-id="${s.id}" title="טען סט זה">▶ טען</button>` : ''}
          ${isActive && dirty && !isBuiltIn ? `<button class="btn-xs btn-outline" data-tset-action="update" data-tset-id="${s.id}" title="עדכן עם התבניות הנוכחיות">עדכן</button>` : ''}
          ${!isBuiltIn ? `<button class="btn-xs btn-outline" data-tset-action="rename" data-tset-id="${s.id}" title="שנה שם">✎</button>` : ''}
          <button class="btn-xs btn-outline" data-tset-action="duplicate" data-tset-id="${s.id}" title="שכפל">⧉</button>
          ${!isBuiltIn ? `<button class="btn-xs btn-danger-outline" data-tset-action="delete" data-tset-id="${s.id}" title="מחק">${SVG_ICONS.trash}</button>` : ''}
        </div>
      </div>`;
    }
    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

function buildTaskSetLoadConfirmMessage(taskSet: TaskSet): string {
  return `טעינת הסט תחליף את תבניות המשימות, את המשימות החד-פעמיות, ואת כללי המרווחים המינימליים. להמשיך?`;
}

/** First-run / emptied-out state for the whole task list. Reuses the shared
 *  `.empty-state` pattern; both CTAs reuse already-wired toolbar actions. */
function renderTaskRulesEmptyState(): string {
  return `<div class="empty-state">
    <div class="empty-icon">${SVG_ICONS.tasks}</div>
    <p>עדיין לא הוגדרו סוגי משימות.</p>
    <p class="text-muted">סוג משימה מגדיר תפקיד חוזר בלוח — כמה משמרות ביום, מי כשיר לאייש אותו, ואילו כללי עומס ומנוחה חלים. צור את הראשון כדי להתחיל, או טען סט מוכן.</p>
    <div class="empty-state-actions">
      <button class="btn-sm btn-primary" data-action="toggle-add-template">➕ צור משימה ראשונה</button>
      <button class="btn-sm btn-outline" data-action="tset-panel-toggle">📋 טען סט ברירת מחדל</button>
    </div>
  </div>`;
}

/** CTA shown inside an expanded task card that has no slots or sub-teams yet.
 *  The creation handoff lands the user here, so the next step — defining who can
 *  fill the task — is obvious rather than buried at the bottom of the card. */
function renderEmptyTaskSlotCTA(templateId: string): string {
  return `<div class="slot-empty-cta">
    <p class="slot-empty-cta-hint">הוסף משבצת אחת לפחות כדי שניתן יהיה לשבץ את המשימה.</p>
    <button class="btn-sm btn-primary" data-action="add-slot" data-tid="${templateId}">➕ הוסף משבצת ראשונה</button>
  </div>`;
}

function renderTemplateCard(tpl: TaskTemplate, pf: PreflightResult): string {
  const isExpanded = expandedTemplateId === tpl.id;
  const relevantFindings = getRelevantFindingsForTemplate(tpl, pf);
  const hasCritical = relevantFindings.some((f) => f.severity === PreflightSeverity.Critical);
  const hasWarning = relevantFindings.some((f) => f.severity === PreflightSeverity.Warning);

  const allSlots = [...tpl.slots];
  for (const st of tpl.subTeams) allSlots.push(...st.slots);
  const totalSlots = allSlots.length;
  const totalPeople = totalSlots * tpl.shiftsPerDay;

  const hasOrphans = hasOrphanedSlotCerts(tpl);
  const alertClass = hasCritical ? 'template-card-error' : hasWarning ? 'template-card-warn' : '';

  let html = `<div class="template-card ${alertClass}" data-template-id="${tpl.id}">
    <div class="template-header" data-action="toggle-template" data-tid="${tpl.id}">
      <div class="template-title">
        ${templateBadge(tpl)}
        <strong>${escHtml(tpl.name)}</strong>
        <span class="text-muted"> · ${tpl.shiftsPerDay} משמרות × ${tpl.durationHours} שע׳ — ${totalPeople} איש/יום</span>
        ${hasCritical ? `<span class="badge badge-sm badge-clickable" style="background:var(--danger)" role="button" tabindex="0" data-action="show-task-findings" data-kind="tpl" data-tid="${tpl.id}" title="הצג פירוט בעיות" aria-label="הצג פירוט בעיות">!</span>` : ''}
        ${hasWarning && !hasCritical ? `<span class="badge badge-sm badge-clickable" style="background:var(--warning)" role="button" tabindex="0" data-action="show-task-findings" data-kind="tpl" data-tid="${tpl.id}" title="הצג פירוט אזהרות" aria-label="הצג פירוט אזהרות">⚠</span>` : ''}
        ${hasOrphans ? '<span class="badge badge-sm badge-orphan">⚠</span>' : ''}
      </div>
      <div class="template-toggles">
        ${tpl.sameGroupRequired ? '<span class="badge badge-sm badge-outline">נדרשת אותה קבוצה</span>' : ''}
        ${tpl.blocksConsecutive ? '' : '<span class="badge badge-sm badge-outline">ניתן לשבץ ברצף</span>'}
        ${tpl.togethernessRelevant ? '<span class="badge badge-sm badge-outline">אי התאמה</span>' : ''}
        ${_restRuleBadge(tpl.restRuleId)}
        <span class="expand-arrow">${isExpanded ? '▼' : '▶'}</span>
      </div>
    </div>`;

  if (isExpanded) {
    html += `<div class="template-body">`;

    // Template properties
    html += `<div class="template-props">
      <section class="tprop-section tprop-section--numeric">
        <h5 class="tprop-section-title">מאפיינים בסיסיים</h5>
        <div class="tprop-grid tprop-grid-numeric">
          <label class="tprop-field">
            <span class="tprop-field-label">שם</span>
            <input class="input-sm" type="text" maxlength="60" data-tpl-field="name" value="${escHtml(tpl.name)}" data-tid="${tpl.id}" />
          </label>
          <label class="tprop-field">
            <span class="tprop-field-label">משך (שעות)</span>
            <input class="input-sm" type="number" step="0.5" min="0.5" data-tpl-field="durationHours" value="${tpl.durationHours}" data-tid="${tpl.id}" />
          </label>
          <label class="tprop-field">
            <span class="tprop-field-label">משמרות/יום</span>
            <input class="input-sm" type="number" min="1" max="48" data-tpl-field="shiftsPerDay" value="${tpl.shiftsPerDay}" data-tid="${tpl.id}" />
          </label>
          <label class="tprop-field">
            <span class="tprop-field-label">שעת התחלה</span>
            <input class="input-sm" type="time" step="3600" data-tpl-field="startHour" value="${String(tpl.startHour).padStart(2, '0')}:00" data-tid="${tpl.id}" />
          </label>
          <label class="tprop-field tprop-field-load">
            <span class="tprop-field-label">רמת עומס (0-1)</span>
            <div class="tprop-field-control">
              <input class="input-sm" type="number" step="0.05" min="0" max="1" data-tpl-field="baseLoadWeight" value="${(tpl.baseLoadWeight ?? 1).toFixed(2)}" data-tid="${tpl.id}" />
              ${renderLoadFormulaControls({ kind: 'base', tpl, disabled: false })}
            </div>
          </label>
        </div>
        ${renderShiftPreview(tpl)}
      </section>

      <section class="tprop-section tprop-section--behavior">
        <h5 class="tprop-section-title">התנהגות</h5>
        <div class="tprop-grid tprop-grid-2 tprop-toggles">
          <label class="checkbox-label"><input type="checkbox" data-tpl-field="sameGroupRequired" data-tid="${tpl.id}" ${tpl.sameGroupRequired ? 'checked' : ''} /> נדרשת אותה קבוצה</label>
          <label class="checkbox-label"><input type="checkbox" data-tpl-field="blocksConsecutive" data-tid="${tpl.id}" ${tpl.blocksConsecutive ? 'checked' : ''} /> חוסם רצף משימות</label>
          <div class="tprop-toggle-cell">
            <label class="checkbox-label"><input type="checkbox" data-tpl-field="togethernessRelevant" data-tid="${tpl.id}" ${tpl.togethernessRelevant ? 'checked' : ''} /> אי התאמה</label>
            <button type="button" class="tprop-help-btn" data-action="toggle-notwith-help" data-help-key="notwith-tpl-${tpl.id}" aria-controls="notwith-tpl-${tpl.id}" aria-expanded="false" aria-label="מה זה אי התאמה?" title="מה זה אי התאמה?">?</button>
          </div>
          <label class="checkbox-label" title="כשמסומן וגם נבחר אחד ממצבי הפיצול בהגדרות האלגוריתם — המערכת רשאית לפצל מופע של המשימה לשתי משמרות שמאויישות על־ידי שני אנשים שונים: כדי לאייש משבצת שאחרת תישאר ריקה, ובמצב 'השלמת איוש ושיפור איכות' גם כדי לשפר את איכות הלוח (עונש הפיצול קובע את הסף)"><input type="checkbox" data-tpl-field="splittable" data-tid="${tpl.id}" ${tpl.splittable ? 'checked' : ''} /> ניתן לפיצול</label>
        </div>
        <div class="sr-hint notwith-help-panel hidden" id="notwith-tpl-${tpl.id}" data-help-panel="notwith-tpl-${tpl.id}" role="note"><span class="notwith-help-text">המתג מפעיל את ההתחשבות באי-התאמה בין משתתפים; את הזוגות עצמם מגדירים בכרטיסי המשתתפים.</span><button type="button" class="notwith-help-go" data-action="goto-participants">פתח מסך משתתפים ‹</button></div>
      </section>
    </div>`;

    // Eligibility: sub-teams + slots, surfaced right under basics (the dominant
    // reason to open a card). Rest-rule / sleep-recovery / load-windows now live
    // in the collapsed "מתקדם" disclosure below.

    // Sub-teams
    if (tpl.subTeams.length > 0) {
      html += '<h4 style="margin:12px 0 8px;">תת-צוותים</h4>';
      for (const st of tpl.subTeams) {
        html += renderSubTeam(tpl.id, st, pf);
      }
    }

    // Top-level slots
    if (tpl.slots.length > 0 || tpl.subTeams.length === 0) {
      html += `<h4 style="margin:12px 0 8px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">משבצות${tpl.subTeams.length > 0 ? ' נוספות' : ''}${
        tpl.slots.length > 0
          ? `<button class="btn-sm btn-outline" data-action="grouped-slot-edit" data-tid="${tpl.id}">✎ עריכה קבוצתית</button>`
          : ''
      }</h4>`;
      html +=
        tpl.slots.length === 0 ? renderEmptyTaskSlotCTA(tpl.id) : renderSlotTable(tpl.id, tpl.slots, undefined, pf);
    }

    // Eligibility actions (constructive only — destructive delete is isolated below).
    html += `<div class="template-actions">
      <button class="btn-sm btn-outline" data-action="add-subteam" data-tid="${tpl.id}">+ תת-צוות</button>
      <button class="btn-sm btn-outline" data-action="add-slot" data-tid="${tpl.id}">+ משבצת</button>
    </div>`;

    // Inline add-slot form
    if (addingSlotTo && addingSlotTo.templateId === tpl.id && !addingSlotTo.subTeamId) {
      html += renderAddSlotForm(tpl.id);
    }

    // Advanced (collapsed): rest-rule + sleep-recovery + load-windows.
    const tplAdvInner = `<div class="tprop-footer-row">
        <label class="tprop-field tprop-field-grow">
          <span class="tprop-field-label">כלל מרווח</span>
          <select class="input-sm" data-tpl-field="restRuleId" data-tid="${tpl.id}">
            <option value=""${!tpl.restRuleId ? ' selected' : ''}>ללא</option>
            ${store
              .getRestRules()
              .map(
                (r) =>
                  `<option value="${r.id}"${tpl.restRuleId === r.id ? ' selected' : ''}>${escHtml(r.label)} (${r.durationHours} שע׳)</option>`,
              )
              .join('')}
          </select>
        </label>
      </div>
      ${_restRuleOrphanNote(tpl.restRuleId)}
      ${renderSleepRecoveryEditor(tpl.sleepRecovery, 'tpl', tpl.id, shiftsForTemplate(tpl))}
      ${renderLoadWindowsEditor(tpl)}`;
    html += renderAdvancedSection('tpl', tpl.id, tplAdvInner, advancedChips(tpl));

    // Danger footer: destructive delete, isolated from the constructive actions.
    html += `<div class="template-actions template-actions--danger">
      <button class="btn-sm btn-outline" data-action="duplicate-template" data-tid="${tpl.id}">⧉ שכפל</button>
      <button class="btn-sm btn-danger-outline" data-action="remove-template" data-tid="${tpl.id}">הסר תבנית</button>
    </div>`;

    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

type LoadFormulaControlTarget =
  | { kind: 'base'; tpl: TaskTemplate; disabled: boolean }
  | { kind: 'window'; tpl: TaskTemplate; window: LoadWindow; disabled: boolean };

function renderLoadFormulaControls(target: LoadFormulaControlTarget): string {
  const { kind, tpl, disabled } = target;
  const formula = kind === 'base' ? tpl.loadFormula : target.window.loadFormula;
  const lwid = kind === 'window' ? ` data-lwid="${target.window.id}"` : '';
  const openBtn = `<button class="btn-xs btn-outline lf-open-btn" type="button" data-action="open-load-formula" data-lf-kind="${kind}" data-tid="${tpl.id}"${lwid}${disabled ? ' disabled' : ''} title="הגדר לפי השוואה" aria-label="הגדר לפי השוואה">🧮</button>`;
  const infoBtn = formula
    ? `<button class="btn-xs btn-outline lf-info-btn" type="button" data-action="toggle-load-formula-info" data-lf-kind="${kind}" data-tid="${tpl.id}"${lwid} title="הצג הסבר" aria-label="הצג הסבר">ℹ️</button>`
    : '';
  return `<span class="lf-controls">${openBtn}${infoBtn}</span>`;
}

function renderLoadFormulaExplanation(tpl: TaskTemplate, kind: 'base' | 'window', windowId: string | null): string {
  const formula =
    kind === 'base' ? tpl.loadFormula : (tpl.loadWindows ?? []).find((w) => w.id === windowId)?.loadFormula;
  if (!formula) return '';

  const templates = new Map<string, TaskTemplate>();
  for (const t of store.getAllTaskTemplates()) templates.set(t.id, t);
  const stale = detectStale(formula, templates);

  const when = formatMetadataDate(formula.computedAt, true);

  const targetHours = formula.targetHours && formula.targetHours > 0 ? formula.targetHours : 1;
  const hoursPhrase = (n: number): string => {
    if (n === 1) return 'שעה אחת';
    if (n === 0.5) return 'חצי שעה';
    if (n === 0.25) return 'רבע שעה';
    if (n === 0.75) return 'שלושת רבעי שעה';
    if (n === 2) return 'שעתיים';
    return `${n} שעות`;
  };
  // "במצב בסיס" is only meaningful if the ref task has (or had) hot windows to distinguish from.
  // Checks current state so drifted refs read naturally.
  const rateText = (snap: LoadFormulaSnapshotEntryLocal & { templateId?: string }): string => {
    if (snap.rate.kind === 'window') {
      return `בחלון החם ${escHtml(snap.rate.windowLabel)} (עומס ${snap.rate.value.toFixed(2)} לשעה)`;
    }
    const refTpl = snap.templateId ? templates.get(snap.templateId) : undefined;
    const currentHasWindows = !!refTpl && (refTpl.loadWindows ?? []).length > 0;
    const where = currentHasWindows ? 'במצב בסיס ' : '';
    return `${where}(עומס ${snap.rate.value.toFixed(2)} לשעה)`;
  };

  // Detect ref tasks that gained hot windows since save — those now need "במצב בסיס" disambiguation
  // that wasn't needed at save time.
  const gainedWindows = (snap: LoadFormulaSnapshotEntry): boolean => {
    if (snap.rate.kind !== 'base') return false;
    if (snap.refHadLoadWindows !== false) return false;
    const refTpl = templates.get(snap.templateId);
    return !!refTpl && (refTpl.loadWindows ?? []).length > 0;
  };
  const gainedList: string[] = [];

  const lhsExtras = formula.lhsExtras ?? [];
  const lhsSnap = formula.lhsExtrasSnapshot ?? [];
  let body = `<div class="lf-info-header">עומס לשעה: <strong>${formula.computedValue.toFixed(2)}</strong></div>`;

  // Intro sentence — state the equation's left side verbally.
  const targetPhrase = hoursPhrase(targetHours) + ' של המשימה';
  if (lhsExtras.length) {
    const parts = lhsExtras
      .map((c, i) => {
        const snap = lhsSnap[i];
        if (!snap) return '';
        if (snap.missing)
          return `${hoursPhrase(c.hours)} של ${escHtml(snap.templateName)} <span class="lf-info-note">(נמחק)</span>`;
        if (gainedWindows(snap) && !gainedList.includes(snap.templateName)) gainedList.push(snap.templateName);
        return `${hoursPhrase(c.hours)} של ${escHtml(snap.templateName)} ${rateText(snap)}`;
      })
      .filter(Boolean);
    body += `<div class="lf-info-intro">${targetPhrase}, בצירוף ${parts.join(' ובצירוף ')}, שקולות בעומס לסכום של:</div>`;
  } else if (targetHours !== 1) {
    body += `<div class="lf-info-intro">${targetPhrase} שקולות בעומס לסכום של:</div>`;
  } else {
    body += `<div class="lf-info-intro">${targetPhrase} שקולה בעומס לסכום של:</div>`;
  }

  body += '<ul class="lf-info-list">';
  let rawSum = 0;
  let lhsSum = 0;
  for (let i = 0; i < formula.components.length; i++) {
    const c = formula.components[i];
    const snap = formula.snapshot[i];
    if (!snap) continue;
    if (snap.missing) {
      body += `<li class="lf-info-row lf-info-missing">${hoursPhrase(c.hours)} של ${escHtml(snap.templateName)} <span class="lf-info-note">(נמחק)</span></li>`;
      continue;
    }
    const product = c.hours * snap.rate.value;
    rawSum += product;
    const driftHint =
      stale.entries[i] &&
      stale.entries[i].currentValue !== null &&
      Math.abs((stale.entries[i].currentValue as number) - snap.rate.value) > 1e-9
        ? ` <span class="lf-info-stale">(העומס כעת ${(stale.entries[i].currentValue as number).toFixed(2)})</span>`
        : '';
    if (gainedWindows(snap) && !gainedList.includes(snap.templateName)) gainedList.push(snap.templateName);
    body += `<li class="lf-info-row">${hoursPhrase(c.hours)} של <strong>${escHtml(snap.templateName)}</strong> ${rateText(snap)} — תרומה <strong>${product.toFixed(2)}</strong>${driftHint}</li>`;
  }
  body += '</ul>';

  // Sum LHS extras (for the arithmetic in the summary below).
  for (let i = 0; i < lhsExtras.length; i++) {
    const c = lhsExtras[i];
    const snap = lhsSnap[i];
    if (!snap || snap.missing) continue;
    lhsSum += c.hours * snap.rate.value;
  }
  const netRaw = rawSum - lhsSum;
  const perHourRaw = netRaw / targetHours;

  // Summary in plain language.
  let summary = `סך תרומות הרכיבים: <strong>${rawSum.toFixed(2)}</strong>`;
  if (lhsExtras.length) {
    summary += `. תרומת הצד העליון הנוסף מקוזזת (${lhsSum.toFixed(2)}), ונשאר ${netRaw.toFixed(2)} עבור ${hoursPhrase(targetHours)} של המשימה`;
  } else if (targetHours !== 1) {
    summary += ` עבור ${hoursPhrase(targetHours)} של המשימה`;
  }
  if (targetHours !== 1 || lhsExtras.length) {
    summary += `. לשעה בודדת: <strong>${perHourRaw.toFixed(2)}</strong>`;
  } else {
    summary += ` לשעה`;
  }
  summary += '.';
  body += `<div class="lf-info-summary">${summary}</div>`;

  if (perHourRaw > 1 + 1e-9) {
    body += `<div class="lf-info-clamp">הערך חורג מהמקסימום (1.00) ולכן נחתך ל-1.00.</div>`;
  } else if (perHourRaw < -1e-9) {
    body += `<div class="lf-info-clamp">הצד העליון גדול מסכום הרכיבים, לכן הערך נחתך ל-0.00.</div>`;
  }
  if (stale.stale) {
    body += `<div class="lf-info-warn">⚠ ערכי העומס של רכיבי ההשוואה השתנו מאז שנשמר. פתח ושמור מחדש כדי לעדכן.</div>`;
  }
  if (gainedList.length) {
    const names = gainedList.map((n) => escHtml(n)).join(', ');
    body += `<div class="lf-info-warn">⚠ ל-${names} נוספו חלונות עומס מוגבר אחרי חישוב העומס. שמור מחדש את ההשוואה כדי לרענן.</div>`;
  }
  body += `<div class="lf-info-when">נשמר ב-${when}</div>`;
  return `<div class="lf-info-popover">${body}</div>`;
}

// Local structural alias used for the rate-text helper (avoids importing the snapshot entry type here).
type LoadFormulaSnapshotEntryLocal = {
  rate: { kind: 'base'; value: number } | { kind: 'window'; windowId: string; windowLabel: string; value: number };
};

function renderLoadWindowsEditor(owner: TaskTemplate | OneTimeTask, opts?: { isOneTime?: boolean }): string {
  const isOt = !!opts?.isOneTime;
  const idAttr = isOt ? `data-ot-id="${owner.id}"` : `data-tid="${owner.id}"`;
  const windows = owner.loadWindows ?? [];
  const taskBlocks = !!owner.blocksConsecutive;
  const blocksTooltip = taskBlocks
    ? 'חוסם רצף משימות מופעל ברמת המשימה — הגדרת חלון לא רלוונטית'
    : 'אם החלון נוגע בקצה המשימה (תחילה או סוף) — הגדר אותו כחוסם רצף בקצה זה';
  let html = `<div class="lw-editor">
    <div class="lw-editor-header">
      <h4 class="lw-editor-title">חלונות עומס מוגבר</h4>
      <span class="lw-editor-count">${windows.length === 0 ? 'אין חלונות' : windows.length === 1 ? 'חלון אחד' : `${windows.length} חלונות`}</span>
    </div>
    <p class="lw-editor-help text-muted">טווחי שעות בהם המשימה נחשבת עומס מוגבר. לכל חלון משקל בין 0 ל-1. אם "חוסם רצף משימות" כבוי, ניתן לסמן חלון ספציפי כחוסם בקצה — החסימה תחול על קצה המשימה (תחילה/סוף) שהחלון נוגע בו.</p>`;

  // Outer .lw-table grid defines column tracks ONCE; .lw-list, .lw-add-form,
  // and every .lw-row inside use `subgrid` so all rows align column-for-column
  // even though they live in two visually distinct wrappers.
  html += '<div class="lw-table">';
  if (windows.length === 0) {
    html += '<p class="lw-empty">לא הוגדרו חלונות עומס. משקל העומס חל על כל המשימה.</p>';
  } else {
    html += `<div class="lw-list" role="list">
      <div class="lw-row lw-row-head" aria-hidden="true">
        <span class="lw-col-label lw-col-time">טווח שעות</span>
        <span class="lw-col-label lw-col-weight">משקל</span>
        <span class="lw-col-label lw-col-blocks">חוסם רצף בקצה</span>
        <span class="lw-col-label lw-col-actions">פעולות</span>
      </div>`;
    for (const w of windows) {
      const checkedAttr = w.blocksAtBoundary ? 'checked' : '';
      const disabledAttr = taskBlocks ? 'disabled' : '';
      // Load-formula 🧮/ℹ️ controls are template-only — `load-formula-modal`
      // operates on TaskTemplate; OneTimeTask has no top-level loadFormula
      // and no compare-and-compute path.
      const formulaControls = isOt
        ? ''
        : renderLoadFormulaControls({ kind: 'window', tpl: owner as TaskTemplate, window: w, disabled: false });
      html += `<div class="lw-row" role="listitem">
        <div class="lw-time">
          <span class="lw-mlabel" aria-hidden="true">טווח שעות</span>
          <input class="input-sm time-24h" type="text" maxlength="5" pattern="[0-2]?[0-9]:[0-5][0-9]" placeholder="HH:mm" data-field="lw-edit-start" data-lwid="${w.id}" value="${fmtHm(w.startHour, w.startMinute)}" aria-label="שעת התחלה" />
          <span class="lw-sep" aria-hidden="true">–</span>
          <input class="input-sm time-24h" type="text" maxlength="5" pattern="[0-2]?[0-9]:[0-5][0-9]" placeholder="HH:mm" data-field="lw-edit-end" data-lwid="${w.id}" value="${fmtHm(w.endHour, w.endMinute)}" aria-label="שעת סיום" />
        </div>
        <div class="lw-weight">
          <span class="lw-mlabel" aria-hidden="true">משקל</span>
          <input class="input-sm lw-weight-input" type="number" step="0.05" min="0" max="1" data-field="lw-edit-weight" data-lwid="${w.id}" value="${w.weight.toFixed(2)}" aria-label="משקל" />
          ${formulaControls}
        </div>
        <div class="lw-blocks">
          <span class="lw-mlabel" aria-hidden="true">חוסם רצף בקצה</span>
          <label class="checkbox-label" title="${escHtml(blocksTooltip)}">
            <input type="checkbox" data-field="lw-edit-blocks" data-lwid="${w.id}" ${checkedAttr} ${disabledAttr} aria-label="חוסם רצף בקצה" />
          </label>
        </div>
        <div class="lw-actions">
          <button class="lw-btn lw-remove" data-action="remove-load-window" ${idAttr} data-lwid="${w.id}" title="מחק חלון" aria-label="מחק חלון">✕</button>
        </div>
      </div>`;
    }
    html += '</div>';
  }

  const addComputeBtn = isOt
    ? ''
    : `<button class="btn-xs btn-outline lf-open-btn" type="button" data-action="add-load-window-and-compute" ${idAttr} title="הוסף וחשב לפי השוואה" aria-label="הוסף וחשב לפי השוואה">🧮</button>`;
  html += `<div class="lw-add-form add-slot-form">
    <div class="lw-add-caption">הוספת חלון חדש</div>
    <div class="lw-row lw-row-add" role="group" aria-label="הוספת חלון חדש">
      <div class="lw-time">
        <span class="lw-mlabel" aria-hidden="true">טווח שעות</span>
        <input class="input-sm time-24h" type="text" maxlength="5" pattern="[0-2]?[0-9]:[0-5][0-9]" placeholder="HH:mm" data-field="lw-start" value="05:00" aria-label="שעת התחלה" />
        <span class="lw-sep" aria-hidden="true">–</span>
        <input class="input-sm time-24h" type="text" maxlength="5" pattern="[0-2]?[0-9]:[0-5][0-9]" placeholder="HH:mm" data-field="lw-end" value="06:30" aria-label="שעת סיום" />
      </div>
      <div class="lw-weight">
        <span class="lw-mlabel" aria-hidden="true">משקל</span>
        <input class="input-sm lw-weight-input" type="number" step="0.05" min="0" max="1" data-field="lw-weight" value="1" aria-label="משקל (0-1)" />
        ${addComputeBtn}
      </div>
      <div class="lw-blocks" aria-hidden="true"></div>
      <div class="lw-actions">
        <button class="lw-btn lw-save" data-action="add-load-window" ${idAttr} title="הוסף חלון" aria-label="הוסף חלון">+</button>
      </div>
    </div>
  </div>
  </div>
  </div>`;

  return html;
}

function renderSubTeam(
  templateId: string,
  st: SubTeamTemplate,
  pf: PreflightResult,
  opts?: { isOneTime?: boolean },
): string {
  const idAttr = opts?.isOneTime ? `data-ot-id="${templateId}"` : `data-tid="${templateId}"`;
  let html = `<div class="subteam-card">
    <div class="subteam-header">
      <div class="subteam-title">
        <strong>${escHtml(st.name)}</strong>
        <span class="text-muted subteam-count">(${st.slots.length === 1 ? 'משבצת אחת' : `${st.slots.length} משבצות`})</span>
      </div>
      <button class="btn-sm btn-danger-outline subteam-del" data-action="remove-subteam" ${idAttr} data-stid="${st.id}" title="מחק תת-צוות" aria-label="מחק תת-צוות">✕</button>
      <div class="subteam-actions">
        <button class="btn-sm btn-outline" data-action="add-slot-subteam" ${idAttr} data-stid="${st.id}">+ משבצת</button>
        ${st.slots.length > 0 ? `<button class="btn-sm btn-outline" data-action="grouped-slot-edit" ${idAttr} data-stid="${st.id}">✎ עריכה קבוצתית</button>` : ''}
      </div>
    </div>`;

  html += renderSlotTable(templateId, st.slots, st.id, pf, opts);

  const isOtMatch = opts?.isOneTime ? addingSlotTo?.isOneTime : !addingSlotTo?.isOneTime;
  if (addingSlotTo && addingSlotTo.templateId === templateId && addingSlotTo.subTeamId === st.id && isOtMatch) {
    html += renderAddSlotForm(templateId, st.id, opts);
  }

  html += `</div>`;
  return html;
}

function renderSlotTable(
  templateId: string,
  slots: SlotTemplate[],
  subTeamId: string | undefined,
  pf: PreflightResult,
  opts?: { isOneTime?: boolean },
): string {
  if (slots.length === 0) return '<p class="text-muted" style="padding:4px 0;">אין משבצות מוגדרות.</p>';

  let html = `<div class="table-responsive"><table class="table table-slots">
    <thead><tr><th>תווית</th><th>דרגות</th><th>הסמכות נדרשות</th><th>הסמכות אסורות</th><th></th></tr></thead>
    <tbody>`;

  const isOtMatch = opts?.isOneTime ? editingSlot?.isOneTime : !editingSlot?.isOneTime;
  const editingHere =
    editingSlot && editingSlot.templateId === templateId && editingSlot.subTeamId === subTeamId && isOtMatch
      ? editingSlot
      : null;

  for (const slot of slots) {
    const finding = pf.findings.find((f) => f.slotId === slot.id);
    const isCritical = finding?.severity === PreflightSeverity.Critical;
    const isWarn = finding?.severity === PreflightSeverity.Warning;
    const findingIcon = finding
      ? `<span class="slot-finding-icon ${isCritical ? 'text-danger' : 'text-warn'}" title="${escAttr(finding.message)}" aria-label="${escAttr(finding.message)}">${isCritical ? '✗' : '⚠'}</span> `
      : '';

    const forbiddenCerts = slot.forbiddenCertifications ?? [];
    const ownerAttr = opts?.isOneTime ? `data-ot-id="${templateId}"` : `data-tid="${templateId}"`;
    const stAttr = subTeamId ? `data-stid="${subTeamId}"` : '';
    const isEditing = editingHere && editingHere.slotId === slot.id;
    const rowClasses = [
      isEditing ? 'slot-row-editing' : '',
      isCritical ? 'slot-row-error' : '',
      isWarn ? 'slot-row-warn' : '',
    ].filter(Boolean);
    const rowClassAttr = rowClasses.length ? ` class="${rowClasses.join(' ')}"` : '';

    html += `<tr${rowClassAttr}>
      <td>${findingIcon}${escHtml(stripLevelText(slot.label))}</td>
      <td>${slot.acceptableLevels.map((e) => levelBadge(e.level) + (e.lowPriority ? '<sup class="lp-badge" title="מוצא אחרון – הדרגה מותרת אך לא מועדפת">⚠</sup>' : '')).join(' ')}</td>
      <td>${slot.requiredCertifications.length > 0 ? slot.requiredCertifications.map((c) => certBadge(c)).join(' ') : '<span class="text-muted">אין</span>'}</td>
      <td>${forbiddenCerts.length > 0 ? forbiddenCerts.map((c) => forbiddenCertBadge(c)).join(' ') : '<span class="text-muted">אין</span>'}</td>
      <td>
        ${
          isEditing
            ? ''
            : `<button class="btn-sm btn-outline" data-action="edit-slot" ${ownerAttr} ${stAttr} data-slotid="${slot.id}" title="ערוך">✎</button>`
        }
        <button class="btn-sm btn-danger-outline" data-action="remove-slot" ${ownerAttr} ${stAttr} data-slotid="${slot.id}" title="מחק">✕</button>
      </td>
    </tr>`;

    if (isEditing) {
      html += `<tr class="slot-edit-row"><td colspan="5">${renderSlotForm('edit', templateId, subTeamId, opts, slot)}</td></tr>`;
    }
  }

  html += '</tbody></table></div>';
  return html;
}

function renderAddSlotForm(templateId: string, subTeamId?: string, opts?: { isOneTime?: boolean }): string {
  return renderSlotForm('add', templateId, subTeamId, opts);
}

function readSlotFormFields(form: Element): Omit<SlotTemplate, 'id'> | null {
  const label = (form.querySelector('[data-field="slot-label"]') as HTMLInputElement)?.value.trim() ?? '';
  if (!label) {
    showToast('תווית המשבצת לא יכולה להיות ריקה', { type: 'error' });
    return null;
  }
  const acceptableLevels: { level: Level; lowPriority?: boolean }[] = [];
  form.querySelectorAll<HTMLElement>('[data-slot-level]').forEach((btn) => {
    const state = btn.dataset.state;
    if (state === 'normal') acceptableLevels.push({ level: parseInt(btn.dataset.slotLevel!) as Level });
    else if (state === 'lowPriority')
      acceptableLevels.push({ level: parseInt(btn.dataset.slotLevel!) as Level, lowPriority: true });
  });
  if (acceptableLevels.length === 0) {
    showToast('יש לבחור לפחות דרגה אחת עבור המשבצת', { type: 'error' });
    return null;
  }
  const certs: string[] = [];
  form.querySelectorAll<HTMLInputElement>('[data-slot-cert]').forEach((cb) => {
    if (cb.checked && cb.dataset.slotCert) certs.push(cb.dataset.slotCert);
  });
  const forbiddenCerts: string[] = [];
  form.querySelectorAll<HTMLInputElement>('[data-slot-forbidden-cert]').forEach((cb) => {
    if (cb.checked && cb.dataset.slotForbiddenCert) forbiddenCerts.push(cb.dataset.slotForbiddenCert);
  });

  const overlap = certs.filter((c) => forbiddenCerts.includes(c));
  if (overlap.length > 0) {
    showToast(`הסמכה לא יכולה להיות גם נדרשת וגם אסורה: ${overlap.map((c) => store.getCertLabel(c)).join(', ')}`, {
      type: 'error',
    });
    return null;
  }

  return {
    label,
    acceptableLevels,
    requiredCertifications: certs,
    forbiddenCertifications: forbiddenCerts.length > 0 ? forbiddenCerts : undefined,
  };
}

function renderSlotForm(
  mode: 'add' | 'edit',
  templateId: string,
  subTeamId?: string,
  opts?: { isOneTime?: boolean },
  initial?: SlotTemplate,
): string {
  const idAttr = opts?.isOneTime ? `data-ot-id="${templateId}"` : `data-tid="${templateId}"`;
  const stAttr = subTeamId ? `data-stid="${subTeamId}"` : '';
  const lpSup = '<sup class="lp-badge" title="מוצא אחרון – הדרגה מותרת אך לא מועדפת">⚠</sup>';

  const initLevelState = new Map<Level, 'off' | 'normal' | 'lowPriority'>();
  if (mode === 'edit' && initial) {
    for (const l of LEVEL_OPTIONS) initLevelState.set(l, 'off');
    for (const e of initial.acceptableLevels) initLevelState.set(e.level, e.lowPriority ? 'lowPriority' : 'normal');
  } else {
    for (const l of LEVEL_OPTIONS) initLevelState.set(l, 'normal');
  }

  const requiredSet = new Set(initial?.requiredCertifications ?? []);
  const forbiddenSet = new Set(initial?.forbiddenCertifications ?? []);

  const labelVal = initial ? escHtml(initial.label) : '';
  const title = mode === 'edit' ? 'ערוך משבצת' : 'הוסף משבצת';
  const confirmAction = mode === 'edit' ? 'confirm-edit-slot' : 'confirm-add-slot';
  const cancelAction = mode === 'edit' ? 'cancel-edit-slot' : 'cancel-add-slot';
  const confirmLabel = mode === 'edit' ? 'שמור' : 'הוסף';
  const slotIdAttr = mode === 'edit' && initial ? `data-slotid="${initial.id}"` : '';
  const formClass = mode === 'edit' ? 'add-slot-form edit-slot-form' : 'add-slot-form';

  return `<div class="${formClass}">
    <h5>${title}</h5>
    <div class="form-row">
      <label>תווית: <input class="input-sm" type="text" maxlength="32" data-field="slot-label" placeholder="למשל #1" value="${labelVal}" /></label>
    </div>
    <div class="form-row">
      <span>דרגות:</span>
      ${LEVEL_OPTIONS.map((l) => {
        const state = initLevelState.get(l) ?? 'normal';
        const inner =
          state === 'off'
            ? `<span class="text-muted">L${l}</span>`
            : levelBadge(l) + (state === 'lowPriority' ? lpSup : '');
        return `<button type="button" class="level-toggle" data-action="cycle-level" data-slot-level="${l}" data-state="${state}">${inner}</button>`;
      }).join('')}
    </div>
    <div class="form-row">
      <span>הסמכות נדרשות:</span>
      ${getCertOptions()
        .map(
          (def) =>
            `<label class="checkbox-label"><input type="checkbox" data-slot-cert="${def.id}" ${requiredSet.has(def.id) ? 'checked' : ''} /> ${escHtml(def.label)}</label>`,
        )
        .join('')}
    </div>
    <div class="form-row">
      <span>הסמכות אסורות:</span>
      ${getCertOptions()
        .map(
          (def) =>
            `<label class="checkbox-label"><input type="checkbox" data-slot-forbidden-cert="${def.id}" ${forbiddenSet.has(def.id) ? 'checked' : ''} /> ${escHtml(def.label)}</label>`,
        )
        .join('')}
    </div>
    <div class="form-row">
      <button class="btn-sm btn-primary" data-action="${confirmAction}" ${idAttr} ${stAttr} ${slotIdAttr}>${confirmLabel}</button>
      <button class="btn-sm btn-outline" data-action="${cancelAction}">ביטול</button>
    </div>
  </div>`;
}

// ─── One-Time Task Renderers ─────────────────────────────────────────────────

function renderAddOneTimeForm(): string {
  // A one-time task is stored independently of the active schedule length: its
  // day is persisted as a scheduledDate offset (7 is the storage ceiling, same
  // as DateUnavailability rules), and the active scheduleDays only governs
  // whether the task falls inside the generation window. So the day picker is
  // NOT clamped to the current scheduleDays — offering all 7 days lets the user
  // define a task for a day the schedule will only later be extended to cover.
  const numDays = store.MAX_SCHEDULE_DAYS;

  // Build day options: יום 1, יום 2, ... יום N
  const dayOptions = Array.from({ length: numDays }, (_, i) => `<option value="${i + 1}">יום ${i + 1}</option>`).join(
    '',
  );

  return `<div class="add-form" id="add-onetime-form">
    <h4>משימה חד-פעמית חדשה</h4>
    <div class="form-row">
      <label>שם: <input class="input-sm" type="text" maxlength="60" data-field="ot-name" placeholder="שם משימה" /></label>
    </div>
    <div class="form-row">
      <label>יום:
        <select class="input-sm" data-field="ot-day">${dayOptions}</select>
      </label>
      <label>שעת התחלה: <input class="input-sm" type="number" min="0" max="23" value="6" data-field="ot-start-hour" /></label>
      <label>דקה: <input class="input-sm" type="number" min="0" max="59" value="0" data-field="ot-start-minute" style="width:60px;" /></label>
      <label>משך (שעות): <input class="input-sm" type="number" step="0.5" min="0.5" value="4" data-field="ot-duration" /></label>
      <label>רמת עומס (0-1): <input class="input-sm" type="number" step="0.05" min="0" max="1" value="${_pendingOtLoadFormula !== undefined ? _pendingOtLoadFormula.computedValue.toFixed(2) : '1'}" data-field="ot-base-load" /><span class="lf-controls"><button class="btn-xs btn-outline lf-open-btn" type="button" data-action="open-load-formula-new" data-lf-target="ot" title="הגדר לפי השוואה" aria-label="הגדר לפי השוואה">🧮</button></span></label>
    </div>
    <div class="form-row">
      <label class="checkbox-label"><input type="checkbox" data-field="ot-samegroup" /> נדרשת אותה קבוצה</label>
      <label class="checkbox-label"><input type="checkbox" data-field="ot-blocks-consecutive" checked /> חוסם רצף משימות</label>
      <label class="checkbox-label"><input type="checkbox" data-field="ot-togetherness" /> אי התאמה</label>
      <label>כלל מרווח: <select class="input-sm" data-field="ot-rest-rule">
        <option value="">ללא</option>
        ${store
          .getRestRules()
          .map((r) => `<option value="${r.id}">${escHtml(r.label)} (${r.durationHours} שע׳)</option>`)
          .join('')}
      </select></label>
    </div>
    <div class="form-row">
      <label>תיאור: <input class="input-sm" type="text" maxlength="200" data-field="ot-desc" placeholder="אופציונלי" style="width:300px;" /></label>
    </div>
    <div class="form-row">
      <button class="btn-sm btn-primary" data-action="confirm-add-onetime">צור</button>
      <button class="btn-sm btn-outline" data-action="cancel-add-onetime">ביטול</button>
    </div>
  </div>`;
}

function renderOneTimeCard(ot: OneTimeTask, pf: PreflightResult): string {
  const isExpanded = expandedOtId === ot.id;
  const schedDate = store.getScheduleDate();
  const otDay = new Date(ot.scheduledDate.getFullYear(), ot.scheduledDate.getMonth(), ot.scheduledDate.getDate());
  const schedStart = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate());
  const dayNum = Math.round((otDay.getTime() - schedStart.getTime()) / 86400000) + 1;
  const dateStr = `יום ${dayNum}`;
  const timeStr = fmtHm(ot.startHour, ot.startMinute);
  const totalMinutes = ot.startMinute + Math.round((ot.durationHours % 1) * 60);
  const endH = ot.startHour + Math.floor(ot.durationHours) + Math.floor(totalMinutes / 60);
  const endM = totalMinutes % 60;
  const endStr = fmtHm(endH % 24, endM);

  const allSlots = [...ot.slots];
  for (const st of ot.subTeams) allSlots.push(...st.slots);
  const totalSlots = allSlots.length;

  const flags: string[] = [];
  if (ot.sameGroupRequired) flags.push('קבוצה');
  if (ot.blocksConsecutive) flags.push('חוסם רצף');
  if (ot.togethernessRelevant) flags.push('אי התאמה');

  const relevantFindings = getRelevantFindingsForOneTime(ot, pf);
  const hasCritical = relevantFindings.some((f) => f.severity === PreflightSeverity.Critical);
  const hasWarning = relevantFindings.some((f) => f.severity === PreflightSeverity.Warning);
  const alertClass = hasCritical ? 'template-card-error' : hasWarning ? 'template-card-warn' : '';

  let html = `<div class="template-card onetime-card ${alertClass}" data-ot-id="${ot.id}">
    <div class="template-header" data-action="toggle-onetime" data-ot-id="${ot.id}">
      <div class="template-title">
        ${templateBadge({ color: ot.color, name: ot.name })}
        <strong>${escHtml(ot.name)}</strong>
        <span class="text-muted" style="font-size:0.85em;">📅 ${dateStr} <span dir="ltr">${timeStr}–${endStr}</span> (${ot.durationHours} שע')</span>
        ${hasCritical ? `<span class="badge badge-sm badge-clickable" style="background:var(--danger)" role="button" tabindex="0" data-action="show-task-findings" data-kind="ot" data-ot-id="${ot.id}" title="הצג פירוט בעיות" aria-label="הצג פירוט בעיות">!</span>` : ''}
        ${hasWarning && !hasCritical ? `<span class="badge badge-sm badge-clickable" style="background:var(--warning)" role="button" tabindex="0" data-action="show-task-findings" data-kind="ot" data-ot-id="${ot.id}" title="הצג פירוט אזהרות" aria-label="הצג פירוט אזהרות">⚠</span>` : ''}
      </div>
      <div class="template-toggles">
        ${flags.length > 0 ? flags.map((f) => `<span class="badge badge-sm badge-outline">${f}</span>`).join('') : ''}
        ${_restRuleBadge(ot.restRuleId)}
        <span class="expand-arrow">${isExpanded ? '▼' : '▶'}</span>
      </div>
    </div>`;

  if (!isExpanded) {
    html += `<div class="template-meta">
      <span class="meta-item">${totalSlots === 1 ? 'משבצת אחת' : `${totalSlots} משבצות`}</span>
      ${ot.description ? `<span class="meta-item text-muted">${escHtml(ot.description)}</span>` : ''}
    </div>`;
  }

  if (isExpanded) {
    html += `<div class="template-body">`;

    // Day options span the full storage ceiling (not the active scheduleDays),
    // matching the add form: a one-time task lives independently of the current
    // schedule length, so the user can keep it on a day the schedule will only
    // later be extended to cover. Math.max guards any legacy value > ceiling.
    const numDays = Math.max(store.MAX_SCHEDULE_DAYS, dayNum);
    const dayOptions = Array.from({ length: numDays }, (_, i) => {
      const d = i + 1;
      return `<option value="${d}"${d === dayNum ? ' selected' : ''}>יום ${d}</option>`;
    }).join('');

    // Properties
    html += `<div class="template-props">
      <label>שם: <input class="input-sm" type="text" maxlength="60" data-ot-field="name" value="${escHtml(ot.name)}" data-ot-id="${ot.id}" /></label>
      <label>יום: <select class="input-sm" data-ot-field="dayNum" data-ot-id="${ot.id}">${dayOptions}</select></label>
      <label>שעת התחלה: <input class="input-sm" type="number" min="0" max="23" data-ot-field="startHour" value="${ot.startHour}" data-ot-id="${ot.id}" /></label>
      <label>דקת התחלה: <input class="input-sm" type="number" min="0" max="59" data-ot-field="startMinute" value="${ot.startMinute}" data-ot-id="${ot.id}" /></label>
      <label>משך (שעות): <input class="input-sm" type="number" step="0.5" min="0.5" data-ot-field="durationHours" value="${ot.durationHours}" data-ot-id="${ot.id}" /></label>
      <label>רמת עומס (0-1): <input class="input-sm" type="number" step="0.05" min="0" max="1" data-ot-field="baseLoadWeight" value="${(ot.baseLoadWeight ?? 1).toFixed(2)}" data-ot-id="${ot.id}" /></label>
      <label class="checkbox-label"><input type="checkbox" data-ot-field="sameGroupRequired" data-ot-id="${ot.id}" ${ot.sameGroupRequired ? 'checked' : ''} /> נדרשת אותה קבוצה</label>
      <label class="checkbox-label"><input type="checkbox" data-ot-field="blocksConsecutive" data-ot-id="${ot.id}" ${ot.blocksConsecutive ? 'checked' : ''} /> חוסם רצף משימות</label>
      <div class="tprop-toggle-cell"><label class="checkbox-label"><input type="checkbox" data-ot-field="togethernessRelevant" data-ot-id="${ot.id}" ${ot.togethernessRelevant ? 'checked' : ''} /> אי התאמה</label><button type="button" class="tprop-help-btn" data-action="toggle-notwith-help" data-help-key="notwith-ot-${ot.id}" aria-controls="notwith-ot-${ot.id}" aria-expanded="false" aria-label="מה זה אי התאמה?" title="מה זה אי התאמה?">?</button></div>
      <label class="checkbox-label" title="כשמסומן וגם נבחר אחד ממצבי הפיצול בהגדרות האלגוריתם — המערכת רשאית לפצל את המשימה לשתי משמרות שמאויישות על־ידי שני אנשים שונים: כדי לאייש משבצת שאחרת תישאר ריקה, ובמצב 'השלמת איוש ושיפור איכות' גם כדי לשפר את איכות הלוח (עונש הפיצול קובע את הסף)"><input type="checkbox" data-ot-field="splittable" data-ot-id="${ot.id}" ${ot.splittable ? 'checked' : ''} /> ניתן לפיצול</label>
      <label>תיאור: <input class="input-sm" type="text" maxlength="200" data-ot-field="description" value="${escHtml(ot.description || '')}" data-ot-id="${ot.id}" /></label>
    </div>`;

    // Pointer revealed by the "?" help button in the "אי התאמה" row above: the
    // toggle only enables SC-9 for this task; the incompatible pairs themselves
    // are defined per-participant on the Participants screen. Hidden until the
    // user taps "?"; the in-panel button navigates there.
    html += `<div class="sr-hint notwith-help-panel hidden" id="notwith-ot-${ot.id}" data-help-panel="notwith-ot-${ot.id}" role="note"><span class="notwith-help-text">המתג מפעיל את ההתחשבות באי-התאמה בין משתתפים; את הזוגות עצמם מגדירים בכרטיסי המשתתפים.</span><button type="button" class="notwith-help-go" data-action="goto-participants">פתח מסך משתתפים ‹</button></div>`;

    // Sub-teams
    if (ot.subTeams.length > 0) {
      html += '<h4 style="margin:12px 0 8px;">תת-צוותים</h4>';
      for (const st of ot.subTeams) {
        html += renderSubTeam(ot.id, st, pf, { isOneTime: true });
      }
    }

    // Top-level slots
    if (ot.slots.length > 0 || ot.subTeams.length === 0) {
      html += `<h4 style="margin:12px 0 8px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">משבצות${ot.subTeams.length > 0 ? ' נוספות' : ''}${
        ot.slots.length > 0
          ? `<button class="btn-sm btn-outline" data-action="grouped-slot-edit" data-ot-id="${ot.id}">✎ עריכה קבוצתית</button>`
          : ''
      }</h4>`;
      html += renderSlotTable(ot.id, ot.slots, undefined, pf, { isOneTime: true });
    }

    // Eligibility actions (constructive only — destructive delete is isolated below).
    html += `<div class="template-actions">
      <button class="btn-sm btn-outline" data-action="add-subteam" data-ot-id="${ot.id}">+ תת-צוות</button>
      <button class="btn-sm btn-outline" data-action="add-slot" data-ot-id="${ot.id}">+ משבצת</button>
    </div>`;

    // Inline add-slot form
    if (addingSlotTo && addingSlotTo.templateId === ot.id && addingSlotTo.isOneTime && !addingSlotTo.subTeamId) {
      html += renderAddSlotForm(ot.id, undefined, { isOneTime: true });
    }

    // Advanced (collapsed): rest-rule + sleep-recovery + load-windows.
    const otAdvInner = `<label>כלל מרווח: <select class="input-sm" data-ot-field="restRuleId" data-ot-id="${ot.id}">
        <option value=""${!ot.restRuleId ? ' selected' : ''}>ללא</option>
        ${store
          .getRestRules()
          .map(
            (r) =>
              `<option value="${r.id}"${ot.restRuleId === r.id ? ' selected' : ''}>${escHtml(r.label)} (${r.durationHours} שע׳)</option>`,
          )
          .join('')}
      </select></label>${_restRuleOrphanNote(ot.restRuleId)}
      ${renderSleepRecoveryEditor(ot.sleepRecovery, 'ot', ot.id, shiftsForOneTime(ot))}
      ${renderLoadWindowsEditor(ot, { isOneTime: true })}`;
    html += renderAdvancedSection('ot', ot.id, otAdvInner, advancedChips(ot));

    // Danger footer: destructive delete, isolated from the constructive actions.
    html += `<div class="template-actions template-actions--danger">
      <button class="btn-sm btn-outline" data-action="duplicate-onetime" data-ot-id="${ot.id}">⧉ שכפל</button>
      <button class="btn-sm btn-danger-outline" data-action="delete-onetime" data-ot-id="${ot.id}">הסר משימה</button>
    </div>`;

    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

// ─── Auto-save helpers ───────────────────────────────────────────────────────

/**
 * Read-and-commit the template properties block. Returns true on success,
 * false if a validation error was shown (commit was skipped).
 *
 * Shared by the change-event auto-save handler. All tpl-field inputs route
 * through here so a single edit reads the whole block and writes one atomic
 * store update — matching the former explicit-save semantics.
 */
function _commitTemplateProps(body: HTMLElement, tid: string): boolean {
  const name = (body.querySelector('[data-tpl-field="name"]') as HTMLInputElement)?.value.trim();
  if (!name) {
    showToast('שם משימה נדרש', { type: 'error' });
    return false;
  }
  if (isTaskNameTaken(name, tid)) {
    showToast(`משימה בשם "${name}" כבר קיימת`, { type: 'error' });
    return false;
  }
  const dur = parseFloat((body.querySelector('[data-tpl-field="durationHours"]') as HTMLInputElement)?.value || '8');
  // parseFloat so that decimals (e.g. 1.7) reach the sanitizer's rounding
  // path and surface as a clamp notification instead of being silently
  // truncated by parseInt.
  const shifts = parseFloat((body.querySelector('[data-tpl-field="shiftsPerDay"]') as HTMLInputElement)?.value || '1');
  const startH = parseFloat((body.querySelector('[data-tpl-field="startHour"]') as HTMLInputElement)?.value || '6');
  const baseLoad = parseFloat(
    (body.querySelector('[data-tpl-field="baseLoadWeight"]') as HTMLInputElement)?.value || '1',
  );
  const sameGroup = (body.querySelector('[data-tpl-field="sameGroupRequired"]') as HTMLInputElement)?.checked || false;
  const blocksConsecutive =
    (body.querySelector('[data-tpl-field="blocksConsecutive"]') as HTMLInputElement)?.checked || false;
  const togethernessRelevant =
    (body.querySelector('[data-tpl-field="togethernessRelevant"]') as HTMLInputElement)?.checked || false;
  const splittable = (body.querySelector('[data-tpl-field="splittable"]') as HTMLInputElement)?.checked || false;
  const restRuleId = (body.querySelector('[data-tpl-field="restRuleId"]') as HTMLSelectElement)?.value || undefined;

  // Detect which of duration/shifts the user actually edited so that — when
  // their product would exceed 24h — the value they typed is the one that
  // gets clamped (least surprise). If both changed (or neither), default to
  // clamping shifts, matching the add-template flow.
  const existingTpl = store.getTaskTemplate(tid);
  const existingDur = existingTpl?.durationHours;
  const existingShifts = existingTpl?.shiftsPerDay;
  const durChanged = existingDur === undefined || Math.abs(dur - existingDur) > 1e-9;
  const shiftsChanged = existingShifts === undefined || Math.round(shifts) !== existingShifts;
  const clampSide: 'durationHours' | 'shiftsPerDay' = durChanged && !shiftsChanged ? 'durationHours' : 'shiftsPerDay';

  const sanitized = store.sanitizeTemplateNumericFields(
    {
      durationHours: dur,
      shiftsPerDay: shifts,
      startHour: startH,
    },
    { clampSide },
  );
  notifyIfClamped({ durationHours: dur, shiftsPerDay: shifts, startHour: startH }, sanitized);

  const clampedBaseLoad = Math.max(0, Math.min(1, baseLoad));
  const existingFormulaValue = existingTpl?.loadFormula?.computedValue;
  const formulaDroppedByManualEdit =
    existingFormulaValue !== undefined && Math.abs(clampedBaseLoad - existingFormulaValue) > 1e-9;
  const sleepRecovery = parseSleepRecoveryInput(body, 'tpl', tid);
  if (sleepRecovery.kind === 'invalid') {
    showToast(sleepRecovery.reason, { type: 'error' });
    return false;
  }
  store.renameTaskTemplate(tid, name);
  store.updateTaskTemplate(tid, {
    durationHours: sanitized.durationHours,
    shiftsPerDay: sanitized.shiftsPerDay,
    startHour: sanitized.startHour,
    baseLoadWeight: clampedBaseLoad,
    ...(formulaDroppedByManualEdit ? { loadFormula: undefined } : {}),
    sameGroupRequired: sameGroup,
    blocksConsecutive,
    togethernessRelevant,
    splittable,
    restRuleId,
    ...(sleepRecovery.kind === 'set' ? { sleepRecovery: sleepRecovery.value } : {}),
    ...(sleepRecovery.kind === 'clear' ? { sleepRecovery: undefined } : {}),
  });
  return true;
}

/** Read-and-commit one-time task properties. Returns false on validation error. */
function _commitOneTimeProps(body: HTMLElement, otId: string): boolean {
  const name = (body.querySelector('[data-ot-field="name"]') as HTMLInputElement)?.value.trim();
  if (!name) {
    showToast('שם משימה נדרש', { type: 'error' });
    return false;
  }
  if (isTaskNameTaken(name, otId)) {
    showToast(`משימה בשם "${name}" כבר קיימת`, { type: 'error' });
    return false;
  }
  const dayNumVal = parseInt((body.querySelector('[data-ot-field="dayNum"]') as HTMLSelectElement)?.value || '1');
  const schedDate = store.getScheduleDate();
  const scheduledDate = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate() + dayNumVal - 1);
  const rawStartHour = parseFloat(
    (body.querySelector('[data-ot-field="startHour"]') as HTMLInputElement)?.value || '6',
  );
  const rawStartMinute = parseFloat(
    (body.querySelector('[data-ot-field="startMinute"]') as HTMLInputElement)?.value || '0',
  );
  const rawDur = parseFloat((body.querySelector('[data-ot-field="durationHours"]') as HTMLInputElement)?.value || '4');
  if (!Number.isFinite(rawDur) || rawDur <= 0) {
    showToast('משך המשימה חייב להיות חיובי (0.5 ומעלה)', { type: 'error' });
    return false;
  }
  const baseLoad = parseFloat(
    (body.querySelector('[data-ot-field="baseLoadWeight"]') as HTMLInputElement)?.value || '1',
  );
  const sameGroup = (body.querySelector('[data-ot-field="sameGroupRequired"]') as HTMLInputElement)?.checked || false;
  const blocksConsecutive =
    (body.querySelector('[data-ot-field="blocksConsecutive"]') as HTMLInputElement)?.checked ?? true;
  const togethernessRelevant =
    (body.querySelector('[data-ot-field="togethernessRelevant"]') as HTMLInputElement)?.checked || false;
  const splittable = (body.querySelector('[data-ot-field="splittable"]') as HTMLInputElement)?.checked || false;
  const otRestRuleId = (body.querySelector('[data-ot-field="restRuleId"]') as HTMLSelectElement)?.value || undefined;
  const desc = (body.querySelector('[data-ot-field="description"]') as HTMLInputElement)?.value.trim();

  const otSanitized = store.sanitizeTemplateNumericFields({ durationHours: rawDur, startHour: rawStartHour });
  const startMinute = Math.max(0, Math.min(59, Math.round(Number.isNaN(rawStartMinute) ? 0 : rawStartMinute)));
  notifyIfClamped(
    { durationHours: rawDur, startHour: rawStartHour, startMinute: rawStartMinute },
    { durationHours: otSanitized.durationHours, startHour: otSanitized.startHour, startMinute },
  );

  const otSleepRecovery = parseSleepRecoveryInput(body, 'ot', otId);
  if (otSleepRecovery.kind === 'invalid') {
    showToast(otSleepRecovery.reason, { type: 'error' });
    return false;
  }
  store.updateOneTimeTask(otId, {
    name,
    scheduledDate,
    startHour: otSanitized.startHour,
    startMinute,
    durationHours: otSanitized.durationHours,
    sameGroupRequired: sameGroup,
    baseLoadWeight: Math.max(0, Math.min(1, baseLoad)),
    blocksConsecutive,
    togethernessRelevant,
    splittable,
    restRuleId: otRestRuleId,
    ...(otSleepRecovery.kind === 'set' ? { sleepRecovery: otSleepRecovery.value } : {}),
    ...(otSleepRecovery.kind === 'clear' ? { sleepRecovery: undefined } : {}),
    description: desc || undefined,
  });
  return true;
}

/** Read-and-commit a rest rule row. Returns false on validation error. */
function _commitRestRule(row: HTMLElement, rrId: string): boolean {
  const label = (row.querySelector('[data-rr-field="label"]') as HTMLInputElement)?.value?.trim();
  const dur = parseFloat((row.querySelector('[data-rr-field="durationHours"]') as HTMLInputElement)?.value);
  if (!label) {
    showToast('שם הכלל לא יכול להיות ריק', { type: 'error' });
    return false;
  }
  const labelKey = label.toLowerCase();
  const duplicate = store.getRestRules().some((r) => r.id !== rrId && r.label.trim().toLowerCase() === labelKey);
  if (duplicate) {
    showToast(`כלל בשם "${label}" כבר קיים`, { type: 'error' });
    return false;
  }
  if (isNaN(dur) || dur < 0.5) {
    showToast('משך לא תקין (מינימום 0.5 שעות)', { type: 'error' });
    return false;
  }
  if (dur > 24) {
    showToast('משך לא תקין (מקסימום 24 שעות)', { type: 'error' });
    return false;
  }
  store.updateRestRule(rrId, { label, durationHours: dur });
  return true;
}

type LoadWindowOwnerRef = { kind: 'tpl'; id: string } | { kind: 'ot'; id: string };

function _getOwnerLoadWindows(owner: LoadWindowOwnerRef): LoadWindow[] | null {
  if (owner.kind === 'tpl') {
    const tpl = store.getTaskTemplate(owner.id);
    return tpl ? tpl.loadWindows || [] : null;
  }
  const ot = store.getOneTimeTask(owner.id);
  return ot ? ot.loadWindows || [] : null;
}

function _setOwnerLoadWindows(owner: LoadWindowOwnerRef, loadWindows: LoadWindow[]): void {
  if (owner.kind === 'tpl') store.updateTaskTemplate(owner.id, { loadWindows });
  else store.updateOneTimeTask(owner.id, { loadWindows });
}

/** Read-and-commit a single load-window row. Returns false on validation error. */
function _commitLoadWindow(body: HTMLElement, owner: LoadWindowOwnerRef, lwid: string): boolean {
  const currentWindows = _getOwnerLoadWindows(owner);
  if (!currentWindows) return false;

  const startInput = body.querySelector(`[data-field="lw-edit-start"][data-lwid="${lwid}"]`) as HTMLInputElement | null;
  const endInput = body.querySelector(`[data-field="lw-edit-end"][data-lwid="${lwid}"]`) as HTMLInputElement | null;
  const weightInput = body.querySelector(
    `[data-field="lw-edit-weight"][data-lwid="${lwid}"]`,
  ) as HTMLInputElement | null;
  const blocksInput = body.querySelector(
    `[data-field="lw-edit-blocks"][data-lwid="${lwid}"]`,
  ) as HTMLInputElement | null;
  if (!startInput || !endInput || !weightInput) return false;

  const ps = parseHm(startInput.value);
  const pe = parseHm(endInput.value);
  const weight = parseFloat(weightInput.value || '1');
  const blocksAtBoundary = blocksInput?.checked ?? false;
  if (!ps || !pe) {
    showToast('שעה לא תקינה — יש להזין בפורמט HH:MM (00:00–23:59)', { type: 'error' });
    return false;
  }
  if (ps.h === pe.h && ps.m === pe.m) {
    showToast('שעת ההתחלה והסיום זהות — יש להגדיר טווח עם משך חיובי', { type: 'error' });
    return false;
  }

  const candidate = { startHour: ps.h, startMinute: ps.m, endHour: pe.h, endMinute: pe.m };
  const conflict = currentWindows.find((w) => w.id !== lwid && loadWindowsOverlap(w, candidate));
  if (conflict) {
    showToast(
      `החלון חופף לחלון קיים (${fmtHm(conflict.startHour, conflict.startMinute)}–${fmtHm(conflict.endHour, conflict.endMinute)}) — השינוי לא נשמר`,
      { type: 'error' },
    );
    return false;
  }

  const nextWindows = currentWindows.map((w) => {
    if (w.id !== lwid) return w;
    const nextWeight = Math.max(0, Math.min(1, weight));
    const next: LoadWindow = {
      ...w,
      startHour: ps.h,
      startMinute: ps.m,
      endHour: pe.h,
      endMinute: pe.m,
      weight: nextWeight,
      blocksAtBoundary,
    };
    // Manual edit of window weight clears any stored formula.
    if (w.loadFormula && Math.abs(nextWeight - w.loadFormula.computedValue) > 1e-9) {
      delete next.loadFormula;
    }
    return next;
  });
  _setOwnerLoadWindows(owner, nextWindows);
  return true;
}

/** Brief visual confirmation that an auto-save landed. */
function _flashSaved(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove('tr-just-saved');
  // Force reflow so re-adding the class restarts the animation.
  void el.offsetWidth;
  el.classList.add('tr-just-saved');
}

/** Build a selector that re-identifies `el` after a rerender replaces it. */
function _buildFocusSelector(el: HTMLElement): string | null {
  const ds = el.dataset;
  if (ds.tplField && ds.tid) return `[data-tpl-field="${ds.tplField}"][data-tid="${ds.tid}"]`;
  if (ds.otField && ds.otId) return `[data-ot-field="${ds.otField}"][data-ot-id="${ds.otId}"]`;
  if (ds.rrField) {
    const row = el.closest<HTMLElement>('[data-rest-rule-id]');
    if (row?.dataset.restRuleId)
      return `[data-rest-rule-id="${row.dataset.restRuleId}"] [data-rr-field="${ds.rrField}"]`;
  }
  if (ds.field && ds.field.startsWith('lw-edit-') && ds.lwid) {
    return `[data-field="${ds.field}"][data-lwid="${ds.lwid}"]`;
  }
  return null;
}

/**
 * Rerender the tab while preserving focus on whichever data-attribute-bearing
 * input currently holds it, and flash the just-saved field's post-render
 * replacement. Skipped while a slot add/edit form is open so the user's
 * unsaved slot-form entries aren't wiped by an unrelated auto-save.
 */
function autoSaveRerender(rerender: () => void, flashSelector?: string | null): void {
  // Slot add/edit forms hold in-DOM unsaved state (label, level buttons, cert
  // checkboxes). A rerender would replace them with values from the store,
  // discarding user input. Defer refresh until the slot form closes.
  if (editingSlot || addingSlotTo) return;

  const active = document.activeElement as HTMLElement | null;
  let focusSelector: string | null = null;
  let selStart: number | null = null;
  let selEnd: number | null = null;
  if (active && (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.tagName === 'TEXTAREA')) {
    focusSelector = _buildFocusSelector(active);
    const inp = active as HTMLInputElement;
    try {
      if (typeof inp.selectionStart === 'number') selStart = inp.selectionStart;
      if (typeof inp.selectionEnd === 'number') selEnd = inp.selectionEnd;
    } catch {
      // selection API not supported on this input type
    }
  }
  rerender();
  if (focusSelector) {
    const el = document.querySelector(focusSelector) as HTMLInputElement | null;
    if (el) {
      el.focus();
      if (selStart !== null && selEnd !== null) {
        try {
          el.setSelectionRange(selStart, selEnd);
        } catch {
          // e.g. type=checkbox doesn't support selection
        }
      }
    }
  }
  if (flashSelector) {
    // Flash happens after rerender so it lands on the post-render element,
    // not on the one about to be replaced.
    const target = document.querySelector(flashSelector) as HTMLElement | null;
    _flashSaved(target);
  }
}

// ─── Event Wiring ────────────────────────────────────────────────────────────

export function wireTaskRulesEvents(
  container: HTMLElement,
  rerender: () => void,
  onNavigateToParticipants?: () => void,
): void {
  initLoadFormulaModal({ onChanged: rerender });
  initAddTemplateModal({
    onCreated: (created) => {
      // Creation handoff: open the new (empty) card and bring it into view so the
      // "add first slot" CTA is immediately visible instead of a half-built task
      // landing silently at the bottom of the list.
      expandedTemplateId = created.id;
      rerender();
      // `rerender()` rebuilds #tab-content as a fresh element, so the captured
      // `container` is now detached — query the live document for the new card.
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-template-id="${created.id}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
  });
  initGroupedSlotEdit({ rerender });

  container.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    if (target.dataset.field !== 'tset-saveas-name' && target.dataset.field !== 'tset-rename-name') return;
    if (!_taskSetFormError) return;
    _taskSetFormError = '';
    target.removeAttribute('aria-invalid');
    const errorEl = container.querySelector('#tset-form-error') as HTMLElement | null;
    if (errorEl) errorEl.textContent = '';
  });

  container.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if ((target as HTMLSelectElement).dataset?.field === 'tpl-display-category') {
      const customInput = container.querySelector<HTMLInputElement>('[data-field="tpl-display-category-custom"]');
      if (customInput) customInput.style.display = (target as HTMLSelectElement).value === '' ? 'inline-block' : 'none';
    }

    // ── Auto-save: template properties ────────────────────────────────
    const tplField = target.closest<HTMLElement>('[data-tpl-field]');
    if (tplField && tplField.dataset.tplField) {
      const tid = tplField.dataset.tid;
      const body = target.closest<HTMLElement>('.template-body');
      if (tid && body) {
        const flashSelector = _buildFocusSelector(tplField);
        if (_commitTemplateProps(body, tid)) {
          autoSaveRerender(rerender, flashSelector);
        }
      }
      return;
    }

    // ── Auto-save: one-time task properties ───────────────────────────
    const otField = target.closest<HTMLElement>('[data-ot-field]');
    if (otField && otField.dataset.otField) {
      const otId = otField.dataset.otId;
      const body = target.closest<HTMLElement>('.template-body');
      if (otId && body) {
        const flashSelector = _buildFocusSelector(otField);
        if (_commitOneTimeProps(body, otId)) {
          autoSaveRerender(rerender, flashSelector);
        }
      }
      return;
    }

    // ── Auto-save: rest rule row ──────────────────────────────────────
    const rrField = target.closest<HTMLElement>('[data-rr-field]');
    if (rrField) {
      const row = target.closest<HTMLElement>('[data-rest-rule-id]');
      const rrId = row?.dataset.restRuleId;
      if (row && rrId) {
        const flashSelector = _buildFocusSelector(rrField);
        if (_commitRestRule(row, rrId)) {
          autoSaveRerender(rerender, flashSelector);
        }
      }
      return;
    }

    // ── Auto-save: load window row ────────────────────────────────────
    const lwField = target.closest<HTMLElement>('[data-field^="lw-edit-"]');
    if (lwField && lwField.dataset.lwid) {
      const lwid = lwField.dataset.lwid;
      const body = target.closest<HTMLElement>('.template-body');
      const tplCard = target.closest<HTMLElement>('.template-card[data-template-id]');
      const otCard = target.closest<HTMLElement>('.template-card[data-ot-id]');
      const owner: LoadWindowOwnerRef | null = tplCard?.dataset.templateId
        ? { kind: 'tpl', id: tplCard.dataset.templateId }
        : otCard?.dataset.otId
          ? { kind: 'ot', id: otCard.dataset.otId }
          : null;
      if (body && owner) {
        const flashSelector = _buildFocusSelector(lwField);
        if (_commitLoadWindow(body, owner, lwid)) {
          autoSaveRerender(rerender, flashSelector);
        }
      }
    }
  });

  container.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;

    // ── Task Set item actions (load/update/rename/duplicate/delete) ──
    const tsetButton = target.closest<HTMLElement>('[data-tset-action]');
    const tsetAction = tsetButton?.dataset.tsetAction;
    if (tsetAction) {
      const tsetId = tsetButton?.dataset.tsetId;
      if (!tsetId) return;
      await _handleTaskSetItemAction(tsetAction, tsetId, rerender);
      return;
    }

    const actionButton = target.closest<HTMLElement>('[data-action]');
    const action = actionButton?.dataset.action;
    if (!action) return;

    switch (action) {
      // "?" button in the "אי התאמה" toggle row reveals/hides the explanation
      // panel sitting just below the toggles grid (keyed by tpl/ot id so it works
      // regardless of where the panel sits in the card DOM).
      case 'toggle-notwith-help': {
        const key = actionButton?.dataset.helpKey;
        if (!key) break;
        const panel = container.querySelector<HTMLElement>(`[data-help-panel="${key}"]`);
        if (!panel) break;
        const nowHidden = panel.classList.toggle('hidden');
        actionButton?.setAttribute('aria-expanded', String(!nowHidden));
        break;
      }
      // In-panel signpost (revealed by the "?" above) to where the pairs live.
      case 'goto-participants': {
        onNavigateToParticipants?.();
        break;
      }
      // ─── Task Sets panel actions ───────────────────────────────────────
      case 'tset-panel-toggle': {
        _taskSetPanelOpen = !_taskSetPanelOpen;
        _taskSetFormMode = 'none';
        _taskSetFormError = '';
        _taskSetRenameTargetId = null;
        rerender();
        break;
      }
      case 'tset-panel-close': {
        _taskSetPanelOpen = false;
        _taskSetFormMode = 'none';
        _taskSetFormError = '';
        _taskSetRenameTargetId = null;
        rerender();
        break;
      }
      case 'tset-new': {
        _taskSetFormMode = 'save-as';
        _taskSetFormError = '';
        rerender();
        break;
      }
      case 'tset-saveas-confirm': {
        const nameInput = container.querySelector<HTMLInputElement>('[data-field="tset-saveas-name"]');
        const descInput = container.querySelector<HTMLInputElement>('[data-field="tset-saveas-desc"]');
        const tsetName = nameInput?.value.trim() ?? '';
        const tsetDesc = descInput?.value.trim() ?? '';
        if (!tsetName) {
          _taskSetFormError = 'השם לא יכול להיות ריק';
          rerender();
          return;
        }
        const result = store.saveCurrentAsTaskSet(tsetName, tsetDesc);
        if (!result) {
          _taskSetFormError = 'סט עם שם זה כבר קיים';
          rerender();
          return;
        }
        _taskSetFormMode = 'none';
        _taskSetFormError = '';
        showToast(`סט "${tsetName}" נשמר בהצלחה`, { type: 'success' });
        rerender();
        break;
      }
      case 'tset-rename-confirm': {
        const renameId = _taskSetRenameTargetId ?? store.getActiveTaskSetId();
        if (!renameId) return;
        const rnInput = container.querySelector<HTMLInputElement>('[data-field="tset-rename-name"]');
        const rdInput = container.querySelector<HTMLInputElement>('[data-field="tset-rename-desc"]');
        const rnName = rnInput?.value.trim() ?? '';
        const rnDesc = rdInput?.value.trim() ?? '';
        if (!rnName) {
          _taskSetFormError = 'השם לא יכול להיות ריק';
          rerender();
          return;
        }
        const rnErr = store.renameTaskSet(renameId, rnName, rnDesc);
        if (rnErr) {
          _taskSetFormError = rnErr;
          rerender();
          return;
        }
        _taskSetFormMode = 'none';
        _taskSetFormError = '';
        _taskSetRenameTargetId = null;
        showToast('הסט עודכן בהצלחה', { type: 'success' });
        rerender();
        break;
      }
      case 'tset-form-cancel': {
        _taskSetFormMode = 'none';
        _taskSetFormError = '';
        _taskSetRenameTargetId = null;
        rerender();
        break;
      }

      // ─── Rest Rule actions ──────────────────────────────────────────
      case 'add-rest-rule': {
        // Suffix the default label so it doesn't collide with existing rules.
        const existing = new Set(store.getRestRules().map((r) => r.label.trim().toLowerCase()));
        let label = 'כלל חדש';
        let n = 2;
        while (existing.has(label.toLowerCase())) {
          label = `כלל חדש (${n++})`;
        }
        const rule = store.addRestRule(label, 5);
        showToast(`כלל "${rule.label}" נוצר בהצלחה`, { type: 'success' });
        rerender();
        break;
      }
      case 'delete-rest-rule': {
        const rrId = actionButton?.dataset.rrId;
        if (!rrId) break;
        const rule = store.getRestRuleById(rrId);
        const confirmed = await showConfirm(
          `למחוק את הכלל "${rule?.label ?? rrId}"? משימות המשויכות אליו ייראו עם אזהרת יתום.`,
          { danger: true, confirmLabel: 'מחק' },
        );
        if (!confirmed) break;
        store.removeRestRule(rrId);
        showToast('הכלל נמחק בהצלחה', { type: 'success' });
        rerender();
        break;
      }

      case 'toggle-template': {
        const tid = actionButton?.closest('[data-tid]')?.getAttribute('data-tid') || actionButton?.dataset.tid!;
        expandedTemplateId = expandedTemplateId === tid ? null : tid;
        expandedOtId = null;
        addingSlotTo = null;
        rerender();
        break;
      }
      case 'toggle-sleep-recovery': {
        const srTarget = actionButton?.dataset.srTarget;
        const srId = actionButton?.dataset.srId;
        if (!srTarget || !srId) break;
        const key = `${srTarget}:${srId}`;
        if (expandedSleepRecovery.has(key)) expandedSleepRecovery.delete(key);
        else expandedSleepRecovery.add(key);
        rerender();
        break;
      }
      case 'toggle-advanced': {
        const advTarget = actionButton?.dataset.advTarget;
        const advId = actionButton?.dataset.advId;
        if (!advTarget || !advId) break;
        const key = `${advTarget}:${advId}`;
        if (expandedAdvanced.has(key)) expandedAdvanced.delete(key);
        else expandedAdvanced.add(key);
        rerender();
        break;
      }
      case 'toggle-shift-chip': {
        if (!actionButton) break;
        const newState = actionButton.getAttribute('aria-checked') !== 'true';
        actionButton.setAttribute('aria-checked', String(newState));
        actionButton.classList.toggle('tprop-shift-chip--selected', newState);
        const block = actionButton.closest<HTMLElement>('[data-tpl-field], [data-ot-field]');
        const body = actionButton.closest<HTMLElement>('.template-body');
        if (!block || !body) break;
        const flashSelector = _buildFocusSelector(block);
        if (block.hasAttribute('data-tpl-field')) {
          const tid = block.dataset.tid;
          if (tid && _commitTemplateProps(body, tid)) autoSaveRerender(rerender, flashSelector);
        } else {
          const otId = block.dataset.otId;
          if (otId && _commitOneTimeProps(body, otId)) autoSaveRerender(rerender, flashSelector);
        }
        break;
      }
      case 'shift-bulk-select':
      case 'shift-bulk-clear': {
        if (!actionButton) break;
        const selectAll = action === 'shift-bulk-select';
        const srBody = actionButton.closest<HTMLElement>('.sr-body');
        if (!srBody) break;
        srBody.querySelectorAll('[data-action="toggle-shift-chip"]').forEach((chip) => {
          chip.setAttribute('aria-checked', String(selectAll));
          (chip as HTMLElement).classList.toggle('tprop-shift-chip--selected', selectAll);
        });
        const block = srBody.querySelector<HTMLElement>(
          '[data-tpl-field="sleepRecoveryShifts"], [data-ot-field="sleepRecoveryShifts"]',
        );
        const body = actionButton.closest<HTMLElement>('.template-body');
        if (!block || !body) break;
        const flashSelector = _buildFocusSelector(block);
        if (block.hasAttribute('data-tpl-field')) {
          const tid = block.dataset.tid;
          if (tid && _commitTemplateProps(body, tid)) autoSaveRerender(rerender, flashSelector);
        } else {
          const otId = block.dataset.otId;
          if (otId && _commitOneTimeProps(body, otId)) autoSaveRerender(rerender, flashSelector);
        }
        break;
      }
      case 'toggle-onetime': {
        const otId = actionButton?.dataset.otId!;
        expandedOtId = expandedOtId === otId ? null : otId;
        expandedTemplateId = null;
        addingSlotTo = null;
        rerender();
        break;
      }
      case 'show-task-findings': {
        const kind = actionButton?.dataset.kind;
        const pf = runPreflight();
        let findings: { message: string; severity: PreflightSeverity }[] = [];
        if (kind === 'tpl') {
          const tid = actionButton?.dataset.tid;
          const tpl = tid ? store.getTaskTemplate(tid) : undefined;
          if (tpl) findings = getRelevantFindingsForTemplate(tpl, pf);
        } else if (kind === 'ot') {
          const otId = actionButton?.dataset.otId;
          const ot = otId ? store.getOneTimeTask(otId) : undefined;
          if (ot) findings = getRelevantFindingsForOneTime(ot, pf);
        }
        if (findings.length === 0) {
          // Findings may have just been resolved by another edit — refresh and bail.
          rerender();
          break;
        }
        const hasCrit = findings.some((f) => f.severity === PreflightSeverity.Critical);
        const list = findings
          .map((f) => {
            const prefix = f.severity === PreflightSeverity.Critical ? '🚫' : '⚠️';
            return `<li>${prefix} ${escHtml(f.message)}</li>`;
          })
          .join('');
        await showAlert('', {
          title: hasCrit ? 'בעיות בהגדרת המשימה' : 'אזהרות בהגדרת המשימה',
          icon: hasCrit ? '🚫' : '⚠️',
          bodyHtml: `<ul class="gm-modal-finding-list">${list}</ul>`,
        });
        break;
      }
      case 'show-readiness-findings': {
        const pf = runPreflight();
        const crits = pf.findings.filter((f) => f.severity === PreflightSeverity.Critical);
        if (crits.length === 0) {
          // Criticals may have just been resolved by another edit — refresh and bail.
          rerender();
          break;
        }
        const list = crits.map((f) => `<li>🚫 ${escHtml(f.message)}</li>`).join('');
        await showAlert('', {
          title: 'לא ניתן ליצור שבצ"ק',
          icon: '🚫',
          bodyHtml: `<ul class="gm-modal-finding-list">${list}</ul>`,
        });
        break;
      }
      case 'open-load-formula': {
        const tid = actionButton?.dataset.tid;
        const kind = actionButton?.dataset.lfKind as 'base' | 'window' | undefined;
        if (!tid || !kind) break;
        if (kind === 'base') {
          openLoadFormulaModal({ kind: 'base', templateId: tid });
        } else {
          const lwid = actionButton?.dataset.lwid;
          if (!lwid) break;
          openLoadFormulaModal({ kind: 'window', templateId: tid, windowId: lwid });
        }
        break;
      }
      case 'open-load-formula-new': {
        // Calculator for the still-inline "add one-time task" form. Reads the
        // in-progress name from the form so the modal header is meaningful.
        // The recurring-template add form lives in add-template-modal and
        // handles its own load-formula picker.
        const form = container.querySelector('#add-onetime-form');
        if (!form) break;
        const name = (form.querySelector('[data-field="ot-name"]') as HTMLInputElement)?.value.trim() || 'משימה חדשה';
        openLoadFormulaModal({
          kind: 'ephemeral',
          name,
          existingFormula: _pendingOtLoadFormula,
          onSave: (formula) => {
            _pendingOtLoadFormula = formula;
          },
        });
        break;
      }
      case 'toggle-load-formula-info': {
        const tid = actionButton?.dataset.tid;
        const kind = actionButton?.dataset.lfKind as 'base' | 'window' | undefined;
        if (!tid || !kind) break;
        const tpl = store.getTaskTemplate(tid);
        if (!tpl) break;
        const lwid = actionButton?.dataset.lwid ?? null;
        // Remove any existing popover; if it was anchored to this same button, we're done (toggle off).
        const existing = document.querySelector('.lf-info-popover-wrap');
        const wasSameAnchor = existing?.getAttribute('data-anchor-key') === `${tid}:${kind}:${lwid ?? ''}`;
        existing?.remove();
        if (wasSameAnchor) break;
        const html = renderLoadFormulaExplanation(tpl, kind, lwid);
        if (!html) break;
        const wrap = document.createElement('div');
        wrap.className = 'lf-info-popover-wrap';
        wrap.setAttribute('data-anchor-key', `${tid}:${kind}:${lwid ?? ''}`);
        wrap.innerHTML = html;
        document.body.appendChild(wrap);
        wrap.style.position = 'fixed';
        wrap.style.zIndex = '9990';
        const anchorBtn = actionButton!;
        const close = () => {
          wrap.remove();
          document.removeEventListener('click', offClick, true);
          document.removeEventListener('keydown', offKey);
          window.removeEventListener('scroll', reposition, true);
          window.removeEventListener('resize', reposition);
        };
        const reposition = () => {
          const btnRect = anchorBtn.getBoundingClientRect();
          // Close if the anchor scrolled fully out of the viewport.
          if (btnRect.bottom < 0 || btnRect.top > window.innerHeight) {
            close();
            return;
          }
          wrap.style.top = `${btnRect.bottom + 6}px`;
          wrap.style.left = `${Math.max(8, btnRect.left - 220)}px`;
        };
        // Dismiss when clicking outside.
        const offClick = (ev: MouseEvent) => {
          if (wrap.contains(ev.target as Node)) return;
          if ((ev.target as HTMLElement).closest('[data-action="toggle-load-formula-info"]')) return;
          close();
        };
        const offKey = (ev: KeyboardEvent) => {
          if (ev.key === 'Escape') close();
        };
        reposition();
        setTimeout(() => {
          document.addEventListener('click', offClick, true);
          document.addEventListener('keydown', offKey);
        }, 0);
        // Capture-phase scroll so we catch any scrolling ancestor, not just window.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        break;
      }
      case 'add-load-window':
      case 'add-load-window-and-compute': {
        const otId = actionButton?.dataset.otId;
        const tid = actionButton?.dataset.tid;
        const owner: LoadWindowOwnerRef | null = otId
          ? { kind: 'ot', id: otId }
          : tid
            ? { kind: 'tpl', id: tid }
            : null;
        if (!owner) break;
        const currentWindows = _getOwnerLoadWindows(owner);
        if (!currentWindows) break;
        const block = actionButton?.closest('.add-slot-form');
        if (!block) break;

        const start = (block.querySelector('[data-field="lw-start"]') as HTMLInputElement | null)?.value || '05:00';
        const end = (block.querySelector('[data-field="lw-end"]') as HTMLInputElement | null)?.value || '06:30';
        const weight = parseFloat(
          (block.querySelector('[data-field="lw-weight"]') as HTMLInputElement | null)?.value || '1',
        );

        const ps = parseHm(start);
        const pe = parseHm(end);
        if (!ps || !pe) {
          showToast('שעה לא תקינה — יש להזין בפורמט HH:MM (00:00–23:59)', { type: 'error' });
          break;
        }
        if (ps.h === pe.h && ps.m === pe.m) {
          showToast('שעת ההתחלה והסיום זהות — יש להגדיר טווח עם משך חיובי', { type: 'error' });
          break;
        }

        const candidate = { startHour: ps.h, startMinute: ps.m, endHour: pe.h, endMinute: pe.m };
        const conflict = currentWindows.find((w) => loadWindowsOverlap(w, candidate));
        if (conflict) {
          showToast(
            `החלון חופף לחלון קיים (${fmtHm(conflict.startHour, conflict.startMinute)}–${fmtHm(conflict.endHour, conflict.endMinute)}) — לא נוסף`,
            { type: 'error' },
          );
          break;
        }

        const newWindow: LoadWindow = {
          id: `lw-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          ...candidate,
          weight: Math.max(0, Math.min(1, weight)),
        };

        _setOwnerLoadWindows(owner, [...currentWindows, newWindow]);
        rerender();
        // The 🧮 compute-by-comparison flow is template-only — load-formula-modal
        // operates on TaskTemplate. The button isn't rendered for one-time tasks,
        // but guard defensively in case of stale clicks.
        if (action === 'add-load-window-and-compute' && owner.kind === 'tpl') {
          openLoadFormulaModal({ kind: 'window', templateId: owner.id, windowId: newWindow.id });
        }
        break;
      }
      case 'remove-load-window': {
        const otId = actionButton?.dataset.otId;
        const tid = actionButton?.dataset.tid;
        const lwid = actionButton?.dataset.lwid!;
        const owner: LoadWindowOwnerRef | null = otId
          ? { kind: 'ot', id: otId }
          : tid
            ? { kind: 'tpl', id: tid }
            : null;
        if (!owner) break;
        const currentWindows = _getOwnerLoadWindows(owner);
        if (!currentWindows) break;
        _setOwnerLoadWindows(
          owner,
          currentWindows.filter((w) => w.id !== lwid),
        );
        rerender();
        showToast('חלון העומס נמחק בהצלחה', {
          type: 'success',
          duration: 6000,
          action: {
            label: 'בטל',
            callback: () => {
              store.undo();
              rerender();
            },
          },
        });
        break;
      }
      case 'add-subteam': {
        const otIdSt = actionButton?.dataset.otId;
        const tidSt = otIdSt || actionButton?.dataset.tid!;
        const name = await showPrompt('הזן שם לתת-צוות:', { title: 'הוספת תת-צוות', maxlength: 32 });
        if (!name) return;
        if (otIdSt) {
          store.addSubTeamToOneTimeTask(otIdSt, name.trim());
        } else {
          store.addSubTeamToTemplate(tidSt, name.trim());
        }
        rerender();
        break;
      }
      case 'remove-subteam': {
        const otIdRst = actionButton?.dataset.otId;
        const tidRst = otIdRst || actionButton?.dataset.tid!;
        const stid = actionButton?.dataset.stid!;
        const okSub = await showConfirm('למחוק את תת-הצוות הזה ואת כל המשבצות שלו?', {
          danger: true,
          title: 'מחיקת תת-צוות',
          confirmLabel: 'מחק',
        });
        if (okSub) {
          if (otIdRst) {
            store.removeSubTeamFromOneTimeTask(otIdRst, stid);
          } else {
            store.removeSubTeamFromTemplate(tidRst, stid);
          }
          rerender();
        }
        break;
      }
      case 'add-slot': {
        const otIdAs = actionButton?.dataset.otId;
        editingSlot = null;
        if (otIdAs) {
          addingSlotTo = { templateId: otIdAs, isOneTime: true };
        } else {
          const tid = actionButton?.dataset.tid!;
          addingSlotTo = { templateId: tid };
        }
        rerender();
        break;
      }
      case 'add-slot-subteam': {
        const otIdAss = actionButton?.dataset.otId;
        const stid = actionButton?.dataset.stid!;
        editingSlot = null;
        if (otIdAss) {
          addingSlotTo = { templateId: otIdAss, subTeamId: stid, isOneTime: true };
        } else {
          const tid = actionButton?.dataset.tid!;
          addingSlotTo = { templateId: tid, subTeamId: stid };
        }
        rerender();
        break;
      }
      case 'edit-slot': {
        const otIdEs = actionButton?.dataset.otId;
        const tidEs = otIdEs || actionButton?.dataset.tid!;
        const stidEs = actionButton?.dataset.stid;
        const slotIdEs = actionButton?.dataset.slotid!;
        addingSlotTo = null;
        editingSlot = {
          templateId: tidEs,
          subTeamId: stidEs,
          slotId: slotIdEs,
          isOneTime: !!otIdEs,
        };
        rerender();
        break;
      }
      case 'cancel-edit-slot': {
        editingSlot = null;
        rerender();
        break;
      }
      case 'cycle-level': {
        const btn = actionButton!;
        const cur = btn.dataset.state;
        const next = cur === 'normal' ? 'lowPriority' : cur === 'lowPriority' ? 'off' : 'normal';
        btn.dataset.state = next;
        const lvl = parseInt(btn.dataset.slotLevel!) as Level;
        const lpSup = '<sup class="lp-badge" title="מוצא אחרון – הדרגה מותרת אך לא מועדפת">⚠</sup>';
        btn.innerHTML =
          next === 'off'
            ? `<span class="text-muted">L${lvl}</span>`
            : levelBadge(lvl) + (next === 'lowPriority' ? lpSup : '');
        break;
      }
      case 'confirm-add-slot': {
        const otIdCas = actionButton?.dataset.otId;
        const tid = otIdCas || actionButton?.dataset.tid!;
        const stid = actionButton?.dataset.stid;
        const form = actionButton?.closest('.add-slot-form')!;
        const slot = readSlotFormFields(form);
        if (!slot) break;

        if (otIdCas) {
          if (stid) {
            store.addSlotToOneTimeSubTeam(otIdCas, stid, slot);
          } else {
            store.addSlotToOneTimeTask(otIdCas, slot);
          }
        } else if (stid) {
          store.addSlotToSubTeam(tid, stid, slot);
        } else {
          store.addSlotToTemplate(tid, slot);
        }
        addingSlotTo = null;
        rerender();
        break;
      }
      case 'cancel-add-slot': {
        addingSlotTo = null;
        rerender();
        break;
      }
      case 'confirm-edit-slot': {
        const otIdCes = actionButton?.dataset.otId;
        const tidCes = otIdCes || actionButton?.dataset.tid!;
        const stidCes = actionButton?.dataset.stid;
        const slotIdCes = actionButton?.dataset.slotid!;
        const form = actionButton?.closest('.add-slot-form')!;
        const patch = readSlotFormFields(form);
        if (!patch) break;

        if (otIdCes) {
          if (stidCes) {
            store.updateSlotInOneTimeSubTeam(otIdCes, stidCes, slotIdCes, patch);
          } else {
            store.updateSlotInOneTimeTask(otIdCes, slotIdCes, patch);
          }
        } else if (stidCes) {
          store.updateSlotInSubTeam(tidCes, stidCes, slotIdCes, patch);
        } else {
          store.updateSlotInTemplate(tidCes, slotIdCes, patch);
        }
        editingSlot = null;
        rerender();
        break;
      }
      case 'remove-slot': {
        const otIdRs = actionButton?.dataset.otId;
        const tidRs = otIdRs || actionButton?.dataset.tid!;
        const stid = actionButton?.dataset.stid;
        const slotId = actionButton?.dataset.slotid!;
        if (otIdRs) {
          if (stid) {
            store.removeSlotFromOneTimeSubTeam(otIdRs, stid, slotId);
          } else {
            store.removeSlotFromOneTimeTask(otIdRs, slotId);
          }
        } else if (stid) {
          store.removeSlotFromSubTeam(tidRs, stid, slotId);
        } else {
          store.removeSlotFromTemplate(tidRs, slotId);
        }
        rerender();
        showToast('המשבצת נמחקה בהצלחה', {
          type: 'success',
          duration: 6000,
          action: {
            label: 'בטל',
            callback: () => {
              store.undo();
              rerender();
            },
          },
        });
        break;
      }
      case 'grouped-slot-edit': {
        const otIdGse = actionButton?.dataset.otId;
        const tidGse = otIdGse || actionButton?.dataset.tid!;
        const stidGse = actionButton?.dataset.stid;
        if (editingSlot || addingSlotTo) {
          editingSlot = null;
          addingSlotTo = null;
          rerender();
        }
        openGroupedSlotEdit({
          kind: otIdGse ? 'ot' : 'tpl',
          id: tidGse,
          subTeamId: stidGse,
        });
        break;
      }
      case 'duplicate-template': {
        const dup = store.duplicateTaskTemplate(actionButton?.dataset.tid!);
        if (dup) {
          expandedTemplateId = dup.id;
          expandedOtId = null;
          showToast('המשימה שוכפלה בהצלחה', { type: 'success' });
          rerender();
        }
        break;
      }
      case 'duplicate-onetime': {
        const dup = store.duplicateOneTimeTask(actionButton?.dataset.otId!);
        if (dup) {
          expandedOtId = dup.id;
          expandedTemplateId = null;
          showToast('המשימה שוכפלה בהצלחה', { type: 'success' });
          rerender();
        }
        break;
      }
      case 'remove-template': {
        const tid = actionButton?.dataset.tid!;
        const tpl = store.getTaskTemplate(tid);
        if (tpl) {
          const okTpl = await showConfirm(`למחוק את התבנית "${tpl.name}"?`, {
            danger: true,
            title: 'מחיקת תבנית',
            confirmLabel: 'מחק',
          });
          if (okTpl) {
            store.removeTaskTemplate(tid);
            if (expandedTemplateId === tid) expandedTemplateId = null;
            rerender();
          }
        }
        break;
      }
      case 'toggle-add-template': {
        openAddTemplateModal();
        break;
      }

      // ─── One-Time Task actions ────────────────────────────────────────
      case 'toggle-add-onetime': {
        showAddOneTime = !showAddOneTime;
        _pendingOtLoadFormula = undefined;
        rerender();
        break;
      }
      case 'confirm-add-onetime': {
        const form = container.querySelector('#add-onetime-form')!;
        const name = (form.querySelector('[data-field="ot-name"]') as HTMLInputElement)?.value.trim();
        if (!name) {
          showToast('שם משימה נדרש', { type: 'error' });
          return;
        }
        if (isTaskNameTaken(name)) {
          showToast(`משימה בשם "${name}" כבר קיימת`, { type: 'error' });
          return;
        }
        const dayNum = parseInt((form.querySelector('[data-field="ot-day"]') as HTMLSelectElement)?.value || '1');
        const schedDate = store.getScheduleDate();
        const scheduledDate = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate() + dayNum - 1);

        const rawStartHour = parseFloat(
          (form.querySelector('[data-field="ot-start-hour"]') as HTMLInputElement)?.value || '6',
        );
        const rawStartMinute = parseFloat(
          (form.querySelector('[data-field="ot-start-minute"]') as HTMLInputElement)?.value || '0',
        );
        const rawDur = parseFloat((form.querySelector('[data-field="ot-duration"]') as HTMLInputElement)?.value || '4');
        if (!Number.isFinite(rawDur) || rawDur <= 0) {
          showToast('משך המשימה חייב להיות חיובי (0.5 ומעלה)', { type: 'error' });
          return;
        }
        const baseLoad = parseFloat(
          (form.querySelector('[data-field="ot-base-load"]') as HTMLInputElement)?.value || '1',
        );
        const sameGroup = (form.querySelector('[data-field="ot-samegroup"]') as HTMLInputElement)?.checked || false;
        const blocksConsecutive =
          (form.querySelector('[data-field="ot-blocks-consecutive"]') as HTMLInputElement)?.checked ?? true;
        const togethernessRelevant =
          (form.querySelector('[data-field="ot-togetherness"]') as HTMLInputElement)?.checked || false;
        const desc = (form.querySelector('[data-field="ot-desc"]') as HTMLInputElement)?.value.trim();
        const otRestRuleId =
          (form.querySelector('[data-field="ot-rest-rule"]') as HTMLSelectElement)?.value || undefined;

        const otSanitized = store.sanitizeTemplateNumericFields({ durationHours: rawDur, startHour: rawStartHour });
        const startMinute = Math.max(0, Math.min(59, Math.round(Number.isNaN(rawStartMinute) ? 0 : rawStartMinute)));
        notifyIfClamped(
          { durationHours: rawDur, startHour: rawStartHour, startMinute: rawStartMinute },
          { durationHours: otSanitized.durationHours, startHour: otSanitized.startHour, startMinute },
        );

        store.addOneTimeTask({
          name,
          scheduledDate,
          startHour: otSanitized.startHour,
          startMinute,
          durationHours: otSanitized.durationHours,
          sameGroupRequired: sameGroup,
          baseLoadWeight: Math.max(0, Math.min(1, baseLoad)),
          loadWindows: [],
          blocksConsecutive,
          togethernessRelevant,
          restRuleId: otRestRuleId,
          subTeams: [],
          slots: [],
          description: desc || undefined,
        });
        showAddOneTime = false;
        _pendingOtLoadFormula = undefined;
        rerender();
        break;
      }
      case 'cancel-add-onetime': {
        showAddOneTime = false;
        _pendingOtLoadFormula = undefined;
        rerender();
        break;
      }
      case 'delete-onetime': {
        const otId = actionButton?.dataset.otId;
        if (!otId) return;
        const ok = await showConfirm('למחוק את המשימה החד-פעמית?', {
          danger: true,
          title: 'מחיקת משימה',
          confirmLabel: 'מחק',
        });
        if (!ok) return;
        if (expandedOtId === otId) expandedOtId = null;
        store.removeOneTimeTask(otId);
        rerender();
        break;
      }
    }
  });
}

// ─── Task Set Item Actions ───────────────────────────────────────────────────

async function _handleTaskSetItemAction(action: string, id: string, rerender: () => void): Promise<void> {
  switch (action) {
    case 'load': {
      const tset = store.getTaskSetById(id);
      if (!tset) return;
      const ok = await showConfirm(buildTaskSetLoadConfirmMessage(tset), {
        danger: true,
        title: 'טעינת סט משימות',
        confirmLabel: 'טען',
      });
      if (!ok) return;
      store.loadTaskSet(id);
      showToast('הסט נטען בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'update': {
      const ok = await showConfirm('לעדכן את הסט לפי תבניות המשימות הנוכחיות?', {
        title: 'עדכון סט',
        confirmLabel: 'עדכן',
      });
      if (!ok) return;
      store.updateTaskSet(id);
      showToast('הסט עודכן בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'rename': {
      _taskSetRenameTargetId = id;
      _taskSetFormMode = 'rename';
      _taskSetFormError = '';
      rerender();
      break;
    }
    case 'duplicate': {
      store.duplicateTaskSet(id);
      showToast('הסט שוכפל בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'delete': {
      const tset = store.getTaskSetById(id);
      if (!tset || tset.builtIn) return;
      const ok = await showConfirm(`למחוק את הסט "${tset.name}"? לא ניתן לבטל פעולה זו.`, {
        danger: true,
        title: 'מחיקת סט',
        confirmLabel: 'מחק',
      });
      if (!ok) return;
      store.deleteTaskSet(id);
      _taskSetFormMode = 'none';
      _taskSetFormError = '';
      _taskSetRenameTargetId = null;
      showToast('הסט נמחק בהצלחה', { type: 'success' });
      rerender();
      break;
    }
  }
}
